/**
 * Landing Page — One-page comprehensive RV Match application.
 * Replaces the previous 2-step quick form. Includes contact, financing,
 * tow vehicle, trade-in, important features, must-haves, plus a hero video.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  User, Mail, Phone, MapPin, DollarSign, Truck, Users, Compass,
  Loader2, Check, Calendar, Home, Car, PawPrint, Baby, Tent,
  UtensilsCrossed, Sun, Droplets, ChevronRight, Sparkles,
  Shield, Gauge, Heart, Wrench, Wallet, CreditCard, Banknote,
  RotateCcw, FileText, Ruler, Search, Calculator, BarChart3, Target,
  ArrowRight, ListChecks, ShieldCheck, BookOpen, Lock, Download,
  Paperclip, Upload, X as XIcon, Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';
import EducationalLightbox, { EduInfoButton } from '../components/EducationalLightbox';
import ProductLadder from '../components/ProductLadder';
import MatchPreviewCards from '../components/MatchPreviewCards';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/258cs0l0_RVMATCHAI-AD-VIDEO.mp4';

// ---------- Option lists ----------
const RV_TYPES = [
  { id: 'travel_trailer', label: 'Travel Trailer', icon: '🚐', desc: 'Towable, popular' },
  { id: 'fifth_wheel',    label: 'Fifth Wheel',    icon: '🏠', desc: 'Spacious, needs truck' },
  { id: 'class_a',        label: 'Class A',        icon: '🚌', desc: 'Largest motorhome' },
  { id: 'class_c',        label: 'Class C',        icon: '🚐', desc: 'Over-cab bed' },
  { id: 'class_b',        label: 'Camper Van',     icon: '🚙', desc: 'Compact & easy' },
  { id: 'toy_hauler',     label: 'Toy Hauler',     icon: '🏍️', desc: 'Garage space' },
  { id: 'pop_up',         label: 'Pop-Up',         icon: '⛺', desc: 'Lightweight' },
  { id: 'truck_camper',   label: 'Truck Camper',   icon: '🛻', desc: 'Goes in truck bed' },
];

const BUDGET_RANGES = [
  { id: 'under_25k',   label: 'Under $25K',   value: 25000 },
  { id: '25k_50k',     label: '$25K - $50K',  value: 50000 },
  { id: '50k_75k',     label: '$50K - $75K',  value: 75000 },
  { id: '75k_100k',    label: '$75K - $100K', value: 100000 },
  { id: '100k_150k',   label: '$100K - $150K', value: 150000 },
  { id: 'over_150k',   label: '$150K+',        value: 200000 },
];

const FINANCING_OPTIONS = [
  { id: 'finance',     label: 'Financing',       icon: CreditCard, desc: 'Monthly payment plan' },
  { id: 'cash',        label: 'Paying Cash',     icon: Banknote,   desc: 'Full payment upfront' },
  { id: 'undecided',   label: 'Not Sure Yet',    icon: Wallet,     desc: 'Want to compare options' },
];

const MONTHLY_PAYMENT_RANGES = [
  { id: '350_550',     label: '$350-550/mo' },
  { id: '550_750',     label: '$550-750/mo' },
  { id: '750_950',     label: '$750-950/mo' },
  { id: '950_1200',    label: '$950-1.2K/mo' },
  { id: '1200_1500',   label: '$1.2K-1.5K/mo' },
  { id: '1500_plus',   label: '$1.5K+/mo' },
];

const DOWN_PAYMENT_RANGES = [
  { id: 'none',        label: '$0 down' },
  { id: 'under_5k',    label: 'Under $5K' },
  { id: '5k_10k',      label: '$5K-10K' },
  { id: '10k_20k',     label: '$10K-20K' },
  { id: '20k_plus',    label: '$20K+' },
];

const CREDIT_OPTIONS = [
  { id: 'excellent',   label: 'Excellent',  range: '740+',   color: 'emerald' },
  { id: 'good',        label: 'Good',       range: '670-739', color: 'green' },
  { id: 'fair',        label: 'Fair',       range: '580-669', color: 'amber' },
  { id: 'poor',        label: 'Poor',       range: 'Below 580', color: 'orange' },
  { id: 'unsure',      label: 'Not Sure',   range: '',         color: 'slate' },
];

const RV_LENGTH_RANGES = [
  { id: 'under_20',  label: 'Under 20\u2032',    max: 20 },
  { id: '20_25',     label: '20\u201325\u2032',  max: 25 },
  { id: '25_30',     label: '25\u201330\u2032',  max: 30 },
  { id: '30_35',     label: '30\u201335\u2032',  max: 35 },
  { id: '35_40',     label: '35\u201340\u2032',  max: 40 },
  { id: 'over_40',   label: '40\u2032+',         max: 0 },
];

const SLEEP_OPTIONS = [
  { id: '2', label: '1-2 People' },
  { id: '4', label: '3-4 People' },
  { id: '6', label: '5-6 People' },
  { id: '8', label: '7+ People' },
];

const CAMPING_STYLES = [
  { id: 'campground',  label: 'Campgrounds',  icon: Tent,    desc: 'Full hookups' },
  { id: 'boondocking', label: 'Off-Grid',     icon: Compass, desc: 'Self-sufficient' },
  { id: 'both',        label: 'Both',         icon: Sparkles, desc: 'Flexible' },
];

const TRIP_TYPES = [
  { id: 'weekend',  label: 'Weekend Trips' },
  { id: 'week',     label: 'Week-Long' },
  { id: 'extended', label: 'Extended Travel' },
  { id: 'fulltime', label: 'Full-Time Living' },
];

const PRIORITY_FEATURES = [
  { id: 'safety',        label: 'Safety',        icon: Shield },
  { id: 'performance',   label: 'Performance',   icon: Gauge },
  { id: 'appearance',    label: 'Appearance',    icon: Sparkles },
  { id: 'comfort',       label: 'Comfort',       icon: Heart },
  { id: 'economy',       label: 'Economy',       icon: DollarSign },
  { id: 'dependability', label: 'Dependability', icon: Wrench },
];

const MUST_HAVE_FEATURES = [
  { id: 'outdoor_kitchen', label: 'Outdoor Kitchen', icon: UtensilsCrossed },
  { id: 'bunk_beds',       label: 'Bunk Beds',       icon: Users },
  { id: 'king_bed',        label: 'King Bed',        icon: Home },
  { id: 'queen_bed',       label: 'Queen Bed',       icon: Home },
  { id: 'solar',           label: 'Solar Ready',     icon: Sun },
  { id: 'washer_dryer',    label: 'Washer/Dryer',    icon: Droplets },
];

// Multi-select floor-plan layouts. Buyers can pick any combination and the
// AI matcher weighs them alongside sleeps + length. Labels stay short so the
// grid stays scannable on mobile.
const FLOOR_PLAN_OPTIONS = [
  { id: 'bunkhouse',       label: 'Bunkhouse' },
  { id: 'rear_kitchen',    label: 'Rear Kitchen' },
  { id: 'rear_living',     label: 'Rear Living' },
  { id: 'front_bedroom',   label: 'Front Bedroom' },
  { id: 'toy_hauler',      label: 'Toy Hauler' },
  { id: 'murphy_bed',      label: 'Murphy Bed' },
  { id: 'couples_rear_bath', label: 'Couples / Rear Bath' },
];

// ---------- Reusable selector ----------
const BoxOption = ({ active, onClick, children, isDark, testId, className = '' }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
      active
        ? 'border-[#F2A900] bg-[#F2A900]/10 shadow-md'
        : isDark
          ? 'border-gray-700 hover:border-gray-500 bg-gray-800/40'
          : 'border-gray-200 hover:border-gray-400 bg-white'
    } ${className}`}
  >
    {children}
  </button>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, isDark, accent = 'amber', eduTopic, onEduOpen, eduLabel }) => {
  const accentColor = {
    amber: 'text-[#F2A900] bg-[#F2A900]/10',
    blue: 'text-blue-500 bg-blue-500/10',
    green: 'text-emerald-500 bg-emerald-500/10',
    purple: 'text-purple-500 bg-purple-500/10',
    rose: 'text-rose-500 bg-rose-500/10',
  }[accent];

  // Scroll-triggered reveal — each SectionCard fades + slides in once it
  // enters the viewport. No data entry required to "unlock" the next section.
  // Uses IntersectionObserver; falls back gracefully if unsupported.
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (revealed) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setRevealed(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [revealed]);

  return (
    <div
      ref={ref}
      className={`relative rounded-2xl border ${isDark ? 'border-white/10 bg-white/[0.03] backdrop-blur-sm' : 'border-gray-200 bg-white'} p-5 sm:p-7 ${isDark ? 'shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]' : 'shadow-sm'} transition-all duration-700 ease-out will-change-transform ${
        revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      data-revealed={revealed}
    >
      <div className="flex items-start gap-3 mb-5">
        <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${accentColor} ${isDark ? 'border border-white/10' : ''}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className={`text-lg sm:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
            {eduTopic && onEduOpen ? (
              <EduInfoButton topic={eduTopic} onOpen={onEduOpen} isDark={isDark} label={eduLabel || 'Learn more'} />
            ) : null}
          </div>
          {subtitle && <p className={`text-sm mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
};

// ============================================================
// LiveMatchPreview — animated AI-match simulation. Cycles through
// "input → analyzing 4,281 configurations → 3 result cards reveal".
// Pulls from a pool of realistic scenarios + RV inventory so the
// preview shows different content each page load (and on each
// internal cycle).
// ============================================================
const _SCENARIOS = [
  {
    inputs: [
      { label: 'Budget', value: '$45k–$65k' },
      { label: 'Tow Vehicle', value: '2022 Ford F-150 · 11,300 lb' },
      { label: 'Camping Style', value: 'Family adventure · 4 people · weekends' },
    ],
    results: [
      { tag: 'BEST MATCH', tagColor: 'from-cyan-400 to-cyan-300', match: 96, name: '2024 Grand Design Imagine 2400BH', cost: '$487/mo · $52,900', why: 'Light enough for your F-150, sleeps 4, weekend-ready' },
      { tag: 'BEST VALUE', tagColor: 'from-emerald-400 to-emerald-300', match: 91, name: '2023 Forest River Wildwood X-Lite', cost: '$412/mo · $44,500', why: 'Lower price, same family fit, proven brand' },
      { tag: 'STRETCH OPTION', tagColor: 'from-indigo-400 to-indigo-300', match: 88, name: '2024 Jayco Jay Feather 22BH', cost: '$542/mo · $58,200', why: 'Premium upgrade if budget allows — better insulation' },
    ],
  },
  {
    inputs: [
      { label: 'Budget', value: '$80k–$120k' },
      { label: 'Tow Vehicle', value: '2023 Ram 2500 · 17,540 lb' },
      { label: 'Camping Style', value: 'Snowbirds · 2 people · 3-month trips' },
    ],
    results: [
      { tag: 'BEST MATCH', tagColor: 'from-cyan-400 to-cyan-300', match: 94, name: '2024 Grand Design Reflection 303RLS', cost: '$821/mo · $89,700', why: 'Built for long-term living, 4-season insulation, residential interior' },
      { tag: 'BEST VALUE', tagColor: 'from-emerald-400 to-emerald-300', match: 89, name: '2023 Keystone Cougar 354FLS', cost: '$704/mo · $76,500', why: 'Same floorplan strengths at $13k less, slightly lighter trim' },
      { tag: 'STRETCH OPTION', tagColor: 'from-indigo-400 to-indigo-300', match: 90, name: '2024 Jayco North Point 377RLBH', cost: '$1,041/mo · $114,300', why: 'Luxury fifth wheel, dual A/C, washer-dryer prep' },
    ],
  },
  {
    inputs: [
      { label: 'Budget', value: '$25k–$35k' },
      { label: 'Tow Vehicle', value: '2021 Toyota 4Runner · 5,000 lb' },
      { label: 'Camping Style', value: 'Couple · weekend boondocking · pets' },
    ],
    results: [
      { tag: 'BEST MATCH', tagColor: 'from-cyan-400 to-cyan-300', match: 95, name: '2024 Rockwood Mini Lite 2104S', cost: '$278/mo · $29,900', why: 'Under your tow rating, off-grid solar prep, pet-friendly layout' },
      { tag: 'BEST VALUE', tagColor: 'from-emerald-400 to-emerald-300', match: 92, name: '2023 No Boundaries 19.1', cost: '$245/mo · $26,400', why: 'Rugged build, lighter footprint, killer value' },
      { tag: 'STRETCH OPTION', tagColor: 'from-indigo-400 to-indigo-300', match: 87, name: '2024 Coachmen Apex Nano 194BHS', cost: '$315/mo · $33,800', why: 'Bunkhouse for pets/gear, slightly heavier but well within tow' },
    ],
  },
  {
    inputs: [
      { label: 'Budget', value: '$120k–$180k' },
      { label: 'RV Type', value: 'Class A motorhome' },
      { label: 'Camping Style', value: 'Full-time travel · retired couple' },
    ],
    results: [
      { tag: 'BEST MATCH', tagColor: 'from-cyan-400 to-cyan-300', match: 97, name: '2024 Tiffin Allegro Open Road 36LA', cost: '$1,402/mo · $159,500', why: 'Diesel-quiet ride, full-time-grade build, easy to maneuver' },
      { tag: 'BEST VALUE', tagColor: 'from-emerald-400 to-emerald-300', match: 92, name: '2023 Newmar Bay Star 3315', cost: '$1,189/mo · $134,900', why: 'Slightly older spec, same comfort tier, $25k savings' },
      { tag: 'STRETCH OPTION', tagColor: 'from-indigo-400 to-indigo-300', match: 91, name: '2024 Entegra Coach Vision XL 36A', cost: '$1,587/mo · $179,200', why: 'Premium chassis, residential fridge, 1.5 baths' },
    ],
  },
  {
    inputs: [
      { label: 'Budget', value: '$60k–$85k' },
      { label: 'RV Type', value: 'Class B camper van' },
      { label: 'Camping Style', value: 'Solo digital nomad · stealth-friendly' },
    ],
    results: [
      { tag: 'BEST MATCH', tagColor: 'from-cyan-400 to-cyan-300', match: 96, name: '2024 Winnebago Solis 59P', cost: '$728/mo · $79,400', why: 'Pop-top roof, lithium power, fits a parking spot' },
      { tag: 'BEST VALUE', tagColor: 'from-emerald-400 to-emerald-300', match: 88, name: '2023 Thor Tellaro 20K', cost: '$612/mo · $66,900', why: 'Same Promaster chassis, less luxe finish, $12k savings' },
      { tag: 'STRETCH OPTION', tagColor: 'from-indigo-400 to-indigo-300', match: 90, name: '2024 Storyteller Overland MODE LT', cost: '$782/mo · $84,600', why: 'Off-grid champion: 4×4 chassis, 400Ah lithium' },
    ],
  },
];

const LiveMatchPreview = () => {
  // Pick a random scenario index on first mount AND each loop iteration
  // so the preview is different every time the user loads the page or
  // sticks around for a few cycles.
  const [scenarioIdx, setScenarioIdx] = React.useState(() => Math.floor(Math.random() * _SCENARIOS.length));
  const [phase, setPhase] = React.useState(0); // 0 inputs, 1 analyzing, 2 results
  const [scanned, setScanned] = React.useState(0);
  const [scanTotal] = React.useState(() => 3800 + Math.floor(Math.random() * 900)); // randomize the analyzed-count too

  React.useEffect(() => {
    let scanInterval;
    let phase1Timer, phase2Timer;
    const tick = () => {
      // Pick a NEW random scenario each loop (not the same as previous)
      setScenarioIdx((prev) => {
        if (_SCENARIOS.length <= 1) return 0;
        let next = Math.floor(Math.random() * _SCENARIOS.length);
        if (next === prev) next = (next + 1) % _SCENARIOS.length;
        return next;
      });
      setPhase(0);
      setScanned(0);
      phase1Timer = setTimeout(() => {
        setPhase(1);
        scanInterval = setInterval(() => {
          setScanned((n) => {
            if (n >= scanTotal) { clearInterval(scanInterval); return scanTotal; }
            return Math.min(scanTotal, n + Math.floor(Math.random() * 280) + 60);
          });
        }, 50);
      }, 2200);
      phase2Timer = setTimeout(() => {
        clearInterval(scanInterval);
        setScanned(scanTotal);
        setPhase(2);
      }, 4800);
    };
    tick();
    const loop = setInterval(tick, 9500);
    return () => {
      clearInterval(loop);
      clearInterval(scanInterval);
      clearTimeout(phase1Timer);
      clearTimeout(phase2Timer);
    };
  }, [scanTotal]);

  const scenario = _SCENARIOS[scenarioIdx] || _SCENARIOS[0];
  const inputs = scenario.inputs;
  const results = scenario.results;

  return (
    <section className="relative bg-[#05070d] text-white py-20 md:py-24 overflow-hidden border-y border-white/5">
      <div className="pointer-events-none absolute inset-0 opacity-50">
        <div className="absolute top-0 right-1/4 w-[28rem] h-[28rem] rounded-full bg-cyan-500 blur-[140px] opacity-30" />
        <div className="absolute bottom-0 left-1/4 w-[28rem] h-[28rem] rounded-full bg-indigo-500 blur-[140px] opacity-30" />
      </div>
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-12">
          <p className="text-xs font-bold tracking-[0.25em] text-cyan-400 mb-3">LIVE AI MATCH</p>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">Watch it work.</h2>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm sm:text-base">
            A real preview of the matching engine — input → analysis → recommendations in seconds.
          </p>
        </div>

        <div className="rounded-3xl border border-white/10 bg-white/[0.025] backdrop-blur-md p-5 sm:p-8 shadow-[0_0_60px_-20px_rgba(56,189,248,0.4)]">
          {/* INPUTS row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" key={`inputs-${scenarioIdx}`}>
            {inputs.map((inp, i) => (
              <div
                key={i}
                className={`rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-all duration-700 ${
                  phase >= 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`}
                style={{ transitionDelay: `${i * 0.2}s` }}
              >
                <p className="text-[10px] font-bold tracking-widest text-slate-500 mb-1.5">{inp.label.toUpperCase()}</p>
                <p className="text-sm font-semibold text-white">{inp.value}</p>
              </div>
            ))}
          </div>

          {/* ANALYZING bar */}
          <div className={`rounded-xl border border-cyan-400/20 bg-cyan-500/[0.04] p-4 mb-6 transition-opacity duration-500 ${phase >= 1 ? 'opacity-100' : 'opacity-30'}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm text-cyan-200 font-medium">
                <Loader2 className={`w-4 h-4 ${phase === 1 ? 'animate-spin' : ''}`} />
                {phase === 1 ? 'Analyzing RV configurations…' : phase >= 2 ? 'Analysis complete' : 'Standby'}
              </div>
              <div className="text-xs font-mono tabular-nums text-cyan-100">
                {scanned.toLocaleString()} / {scanTotal.toLocaleString()}
              </div>
            </div>
            <div className="h-1.5 bg-cyan-900/40 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 via-sky-400 to-indigo-400 transition-[width] duration-200"
                style={{ width: `${Math.min(100, (scanned / scanTotal) * 100)}%` }}
              />
            </div>
          </div>

          {/* RESULTS row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" key={`results-${scenarioIdx}`}>
            {results.map((r, i) => (
              <div
                key={i}
                className={`rounded-xl border border-white/10 bg-white/[0.04] p-5 transition-all duration-700 ${
                  phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                }`}
                style={{ transitionDelay: `${i * 0.15}s` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest text-[#05070d] bg-gradient-to-r ${r.tagColor}`}>
                    {r.tag}
                  </span>
                  <span className="text-xs font-mono text-slate-300">{r.match}% match</span>
                </div>
                <p className="font-bold text-white text-sm leading-snug mb-2">{r.name}</p>
                <p className="text-xs text-slate-400 mb-3">{r.why}</p>
                <p className="text-xs font-mono text-cyan-300">{r.cost}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Mock dealer dashboard for the marketing section
const DealerDashboardMock = () => (
  <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c1224] to-[#06080f] p-1 shadow-[0_30px_80px_-20px_rgba(56,189,248,0.45)]">
    <div className="rounded-xl bg-[#05070d] p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
        </div>
        <div className="text-[10px] text-slate-500 font-mono">dealer.rvmatch.ai</div>
      </div>
      <div className="text-xs text-slate-400 mb-3">Live qualified leads</div>
      <div className="space-y-2.5">
        {[
          { name: 'Sarah Mitchell', score: 94, ctx: 'Family of 4 · F-150 · $50–60k', tag: 'HOT', tagColor: 'bg-rose-500' },
          { name: 'James Thompson', score: 86, ctx: 'Couple · Tundra · $35–50k', tag: 'WARM', tagColor: 'bg-amber-500' },
          { name: 'Priya Patel', score: 78, ctx: 'Solo · Crosstrek · Class B', tag: 'WARM', tagColor: 'bg-amber-500' },
        ].map((l, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-indigo-500 flex items-center justify-center text-[10px] font-bold text-[#05070d]">
              {l.name.split(' ').map(s=>s[0]).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold text-white truncate">{l.name}</p>
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold text-white ${l.tagColor}`}>{l.tag}</span>
              </div>
              <p className="text-[11px] text-slate-500 truncate">{l.ctx}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-mono font-bold text-cyan-300">{l.score}</p>
              <p className="text-[9px] text-slate-500">close prob.</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg bg-gradient-to-r from-cyan-500/10 to-indigo-500/10 border border-cyan-400/20 p-3">
        <div className="text-[10px] font-bold tracking-widest text-cyan-300 mb-1">AI TALKING POINTS · Sarah Mitchell</div>
        <p className="text-xs text-slate-300 leading-relaxed">
          "Light bunkhouse trailer under 7,500 lbs · pre-approved · weekend camper near Albany. Recommend Imagine 2400BH or Wildwood X-Lite."
        </p>
      </div>
    </div>
  </div>
);

// ---------- Page ----------
const LandingPage = ({ embedMode = false, hideSignInPrompt = false, advisorSlug = null }) => {
  const { dealerSlug } = useParams();
  const navigate = useNavigate();
  // The homepage is a single cohesive cinematic experience — every section
  // (hero, AI mock, brand showcase, tools, ebooks, form) lives on the same
  // #05070d palette. We lock isDark=true here so the form internals and
  // section cards always render with proper dark contrast, regardless of
  // the user's global theme preference.
  useTheme();
  const isDark = true;

  // Effective slug used for lead attribution. Starts from the URL param and
  // gets updated to the CANONICAL slug once /api/dealer/by-slug resolves —
  // so if the dealer renamed from /old to /new, a visitor typing /old still
  // gets attributed to that dealer AND is silently redirected to /new so
  // shared links stay clean.
  const [effectiveDealerSlug, setEffectiveDealerSlug] = useState(dealerSlug || advisorSlug || null);

  useEffect(() => {
    // First-touch ad attribution for direct visits (fbclid/gclid/utm_* and an
    // external referrer) — stored once; later values never overwrite earlier.
    try {
      const keys = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
      const found = {};
      const q = new URLSearchParams(window.location.search);
      keys.forEach((k) => { const v = q.get(k); if (v) found[k] = v; });
      if (document.referrer && !document.referrer.startsWith(window.location.origin)) {
        found.referrer = document.referrer;
      }
      if (Object.keys(found).length) {
        const prev = JSON.parse(sessionStorage.getItem('rigready.attrib') || '{}');
        sessionStorage.setItem('rigready.attrib', JSON.stringify({ ...found, ...prev }));
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Advisor attribution can come from the URL path (/:dealerSlug) or, in
    // embed mode (e.g. /rigready?advisor=slug), from the advisorSlug prop.
    const sourceSlug = dealerSlug || advisorSlug;
    if (!sourceSlug) {
      setEffectiveDealerSlug(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/dealer/by-slug/${sourceSlug}`);
        if (cancelled) return;
        const canonical = res?.data?.slug;
        if (canonical) {
          setEffectiveDealerSlug(canonical);
          // Silent redirect if URL slug is historical (i.e. differs from canonical)
          if (dealerSlug && canonical !== dealerSlug) {
            navigate(`/${canonical}`, { replace: true });
          }
        } else {
          setEffectiveDealerSlug(sourceSlug);
        }
      } catch {
        // 404 or network — attribution silently falls back to super admin.
        setEffectiveDealerSlug(sourceSlug);
      }
    })();
    return () => { cancelled = true; };
  }, [dealerSlug, advisorSlug, navigate]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Contact
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [zipcode, setZipcode] = useState('');

  // RV preferences
  const [rvType, setRvType] = useState('');
  const [toyHaulerStyle, setToyHaulerStyle] = useState('');
  const [rvLength, setRvLength] = useState('');
  const [budget, setBudget] = useState('');
  const [sleeps, setSleeps] = useState('');
  const [campingStyle, setCampingStyle] = useState('');
  const [tripType, setTripType] = useState('');
  const [hasKids, setHasKids] = useState(null);
  const [hasPets, setHasPets] = useState(null);

  // Financing
  const [financing, setFinancing] = useState('');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const [downPayment, setDownPayment] = useState('');
  const [credit, setCredit] = useState('');
  const [preApproved, setPreApproved] = useState(null);

  // Tow vehicle
  const [hasTowVehicle, setHasTowVehicle] = useState(null);
  const [towVehicle, setTowVehicle] = useState({ year: '', make: '', model: '', capacity: '', payload: '', vin: '' });
  const [vehicleLookupStatus, setVehicleLookupStatus] = useState({ loading: false, source: null, confidence: null, notes: null });
  const [stickerBusy, setStickerBusy] = useState(false);
  // Tow-first flow: reactively toggled from the hero "Tow Vehicle
  // Check" CTA. Persisted to sessionStorage so a page refresh mid-flow
  // keeps the tow-only view; auto-cleared once the user has a tow
  // vehicle year/make/model.
  const [towFirstMode, setTowFirstMode] = useState(() => {
    try { return typeof window !== 'undefined' && window.sessionStorage.getItem('rvm.startAtTow') === '1'; }
    catch { return false; }
  });

  // Trade-in
  const [hasTrade, setHasTrade] = useState(null);
  const [tradeInfo, setTradeInfo] = useState({ year: '', make: '', model: '', monthlyPayment: '', lienholder: '', notes: '' });

  // Features
  const [priorityFeatures, setPriorityFeatures] = useState([]);
  const [mustHaves, setMustHaves] = useState([]);
  const [floorPlans, setFloorPlans] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');
  // Optional file attachments — picked here, uploaded after lead is created
  // so we can associate them with the new session_id. Array of File objects.
  const [pendingAttachments, setPendingAttachments] = useState([]);

  // Educational lightbox — string topic name when open, null when closed.
  // Wired into SectionCard headers via the EduInfoButton.
  const [eduTopic, setEduTopic] = useState(null);
  const openEdu = (topic) => setEduTopic(topic);
  const closeEdu = () => setEduTopic(null);

  // Refer-a-friend $50 off VIP — detect ?ref=ABCD1234 in URL and look up the
  // referrer's first name to show a friendly invitation banner.
  const [referrerInfo, setReferrerInfo] = useState(null);
  React.useEffect(() => {
    let active = true;
    try {
      const url = new URLSearchParams(window.location.search);
      const fromUrl = (url.get('ref') || '').trim();
      if (fromUrl) {
        try { localStorage.setItem('rvmatch_referrer_ref', fromUrl.toUpperCase()); } catch { /* noop */ }
      }
      const code = (fromUrl || localStorage.getItem('rvmatch_referrer_ref') || '').toUpperCase();
      if (code && code.length === 8) {
        axios.get(`${API}/referrals/lookup/${code}`)
          .then((res) => { if (active && res.data?.valid) setReferrerInfo(res.data); })
          .catch(() => {});
      }
    } catch { /* ignore */ }
    return () => { active = false; };
  }, []);

  // Email-blur "Welcome back" detection — if the email already has results,
  // we surface a banner that lets the user skip the form and view their
  // existing matches (they can dismiss to do a full retake instead).
  const [existingApplicant, setExistingApplicant] = useState(null);
  const [existingDismissed, setExistingDismissed] = useState(false);
  const [existingChecking, setExistingChecking] = useState(false);

  const handleEmailBlur = async () => {
    const e = (email || '').trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return;
    if (existingDismissed) return;
    setExistingChecking(true);
    try {
      const res = await axios.get(`${API}/profile/email-check`, { params: { email: e } });
      if (res.data?.found && res.data?.has_matches) {
        setExistingApplicant(res.data);
      }
    } catch { /* silent */ }
    finally { setExistingChecking(false); }
  };

  // Autosave: restore on mount + save when RV type is picked (or any change after)
  const STORAGE_KEY = 'rvmatch_form_draft_v2';

  // Restore draft once on mount
  // If the user clicked the "Free Tow Vehicle Check" hero CTA, scroll
  // them straight to the Tow section on mount and pre-open it via the
  // startAtTow flag (handled in the show memo above). The flag is
  // cleared once the tow section reports a real vehicle so subsequent
  // page loads flow through the normal Contact → RV Type sequence.
  React.useEffect(() => {
    let flagged = false;
    try {
      flagged = window.sessionStorage.getItem('rvm.startAtTow') === '1';
    } catch { /* noop */ }
    if (!flagged) return;
    // Give React one frame to render the tow section (it's gated on
    // the memoized `show` object), then scroll it into view.
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="tow-vin"]')
        || document.getElementById('start-application')
        || document.querySelector('[data-section="tow"]');
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 200);
    return () => clearTimeout(t);
  }, []);

  // Tow-first mode is NO LONGER auto-cleared when year/make/model
  // fill — that would rip the tow-check overview off screen before
  // the user has time to read it. Instead we stay in tow-first mode
  // until the user explicitly clicks the "Continue to your RV match"
  // button (rendered below the tow section when the lookup is done),
  // OR they change RV type / do something that implies they're moving
  // on. See ``continueFromTowCheck`` below.
  const continueFromTowCheck = () => {
    setTowFirstMode(false);
    try { window.sessionStorage.removeItem('rvm.startAtTow'); } catch { /* noop */ }
    // Scroll gently to the Contact section (which now appears).
    setTimeout(() => {
      const el = document.querySelector('[data-testid="input-first-name"]')
        || document.getElementById('start-application');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  // Tow-first mode: assume the user HAS a tow vehicle (they clicked the
  // Tow Vehicle Check CTA). This surfaces the VIN + sticker inputs
  // immediately, rather than making them tap "Yes I have one" first.
  React.useEffect(() => {
    if (towFirstMode && hasTowVehicle === null) {
      setHasTowVehicle(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [towFirstMode]);

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.firstName) setFirstName(d.firstName);
      if (d.lastName) setLastName(d.lastName);
      if (d.email) setEmail(d.email);
      if (d.phone) setPhone(d.phone);
      if (d.zipcode) setZipcode(d.zipcode);
      if (d.rvType) setRvType(d.rvType);
      if (d.toyHaulerStyle) setToyHaulerStyle(d.toyHaulerStyle);
      if (d.rvLength) setRvLength(d.rvLength);
      if (d.budget) setBudget(d.budget);
      if (d.sleeps) setSleeps(d.sleeps);
      if (d.campingStyle) setCampingStyle(d.campingStyle);
      if (d.tripType) setTripType(d.tripType);
      if (d.hasKids !== undefined) setHasKids(d.hasKids);
      if (d.hasPets !== undefined) setHasPets(d.hasPets);
      if (d.financing) setFinancing(d.financing);
      if (d.monthlyPayment) setMonthlyPayment(d.monthlyPayment);
      if (d.downPayment) setDownPayment(d.downPayment);
      if (d.credit) setCredit(d.credit);
      if (d.preApproved !== undefined) setPreApproved(d.preApproved);
      if (d.hasTowVehicle !== undefined) setHasTowVehicle(d.hasTowVehicle);
      if (d.towVehicle) setTowVehicle(d.towVehicle);
      if (d.hasTrade !== undefined) setHasTrade(d.hasTrade);
      if (d.tradeInfo) setTradeInfo(d.tradeInfo);
      if (Array.isArray(d.priorityFeatures)) setPriorityFeatures(d.priorityFeatures);
      if (Array.isArray(d.mustHaves)) setMustHaves(d.mustHaves);
      if (Array.isArray(d.floorPlans)) setFloorPlans(d.floorPlans);
      if (d.additionalNotes) setAdditionalNotes(d.additionalNotes);
    } catch { /* ignore corrupt drafts */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle ?edit=<session_id> (prefill from server) and ?redo=true (force fresh blank)
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const editSid = params.get('edit');
      const redo = params.get('redo');

      if (redo === 'true') {
        // Force a clean slate — ignore any autosaved draft
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        // Reset every field to default
        setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setZipcode('');
        setRvType(''); setRvLength(''); setBudget(''); setSleeps('');
        setCampingStyle(''); setTripType(''); setHasKids(null); setHasPets(null);
        setFinancing(''); setMonthlyPayment(''); setDownPayment(''); setCredit(''); setPreApproved(null);
        setHasTowVehicle(null); setTowVehicle({ year: '', make: '', model: '', capacity: '', payload: '', vin: '' });
        setHasTrade(null); setTradeInfo({ year: '', make: '', model: '', monthlyPayment: '', lienholder: '', notes: '' });
        setPriorityFeatures([]); setMustHaves([]); setFloorPlans([]); setAdditionalNotes('');
        return;
      }

      if (editSid) {
        // Load existing application from server and prefill
        axios.get(`${API}/leads/by-session/${editSid}`).then((res) => {
          const lead = res.data || {};
          const mc = lead.matching_criteria || {};
          const tow = lead.tow_vehicle || mc.tow_vehicle || {};
          if (lead.first_name) setFirstName(lead.first_name);
          if (lead.last_name) setLastName(lead.last_name);
          if (lead.email) setEmail(lead.email);
          if (lead.phone) setPhone(lead.phone);
          if (lead.zipcode) setZipcode(lead.zipcode);
          if (mc.rv_type) setRvType(mc.rv_type);
          if (mc.rv_length) setRvLength(mc.rv_length);
          // Best-effort budget bucket lookup (matches BUDGET_RANGES values)
          if (mc.budget_max) {
            const bucket = (BUDGET_RANGES.find(b => b.value === mc.budget_max) || {}).id;
            if (bucket) setBudget(bucket);
          }
          if (mc.sleep_capacity) setSleeps(String(mc.sleep_capacity));
          if (mc.camping_style) setCampingStyle(mc.camping_style);
          if (mc.trip_type) setTripType(mc.trip_type);
          if (mc.has_kids !== undefined && mc.has_kids !== null) setHasKids(mc.has_kids);
          if (mc.has_pets !== undefined && mc.has_pets !== null) setHasPets(mc.has_pets);
          if (Array.isArray(mc.must_have_features)) setMustHaves(mc.must_have_features);
          if (Array.isArray(mc.floor_plans)) setFloorPlans(mc.floor_plans);
          if (tow && tow.make) {
            setHasTowVehicle(true);
            setTowVehicle({
              year: String(tow.year || ''),
              make: tow.make || '',
              model: tow.model || '',
              capacity: tow.tow_capacity || '',
              payload: tow.payload || '',
              vin: tow.vin || '',
            });
          }
          if (lead.additional_notes) setAdditionalNotes(lead.additional_notes);
          toast.success('Loaded your previous answers — tweak and resubmit.');
          // Scroll to contact so they see the prefilled banner
          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        }).catch(() => {
          toast.error("Couldn't load your previous application — starting fresh.");
        });
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save draft whenever RV type is set (and any later change)
  React.useEffect(() => {
    if (!rvType) return; // don't autosave until user has expressed intent
    const draft = {
      firstName, lastName, email, phone, zipcode,
      rvType, toyHaulerStyle, rvLength, budget, sleeps, campingStyle, tripType, hasKids, hasPets,
      financing, monthlyPayment, downPayment, credit, preApproved,
      hasTowVehicle, towVehicle, hasTrade, tradeInfo,
      priorityFeatures, mustHaves, floorPlans, additionalNotes,
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(draft)); } catch { /* quota */ }
  }, [
    firstName, lastName, email, phone, zipcode,
    rvType, toyHaulerStyle, rvLength, budget, sleeps, campingStyle, tripType, hasKids, hasPets,
    financing, monthlyPayment, downPayment, credit, preApproved,
    hasTowVehicle, towVehicle, hasTrade, tradeInfo,
    priorityFeatures, mustHaves, floorPlans, additionalNotes,
  ]);

  const formatPhone = (v) => {
    const n = v.replace(/\D/g, '');
    if (n.length <= 3) return n;
    if (n.length <= 6) return `(${n.slice(0, 3)}) ${n.slice(3)}`;
    return `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6, 10)}`;
  };

  const toggleFromList = (list, setList, id) => {
    setList(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  };

  const isTowable = useMemo(
    () => (
      // In tow-first mode, the user came from the "Tow Vehicle Check"
      // free tool — they haven't picked an RV type yet, but we still
      // need the Tow section to render so they can look up their truck.
      towFirstMode
      || ['travel_trailer', 'fifth_wheel', 'toy_hauler', 'pop_up', 'truck_camper'].includes(rvType)
    ),
    [rvType, towFirstMode]
  );

  // Progressive section reveal — Contact + RV Type are ALWAYS visible.
  // Each later section unlocks when the previous block is complete.
  // The SectionCard component independently handles fade-in animation
  // when scrolled into view, so revealed sections still feel cinematic.
  const show = useMemo(() => {
    const phoneDigits = (phone || '').replace(/\D/g, '');
    const emailValid = !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const contactDone = !!firstName.trim() && !!lastName.trim() && emailValid && phoneDigits.length >= 10 && (zipcode || '').length >= 5;

    const rvTypeDone = !!rvType;
    const rvLengthDone = !!rvLength;
    const budgetDone = !!budget;
    const financingBaseDone = !!financing;
    const financingFullDone =
      financing === 'finance'
        ? !!monthlyPayment && !!downPayment && !!credit && preApproved !== null
        : financingBaseDone;

    const travelDone =
      !!sleeps && !!campingStyle && !!tripType && hasKids !== null && hasPets !== null;

    const towDone = !isTowable
      ? true
      : (hasTowVehicle === false ||
         (hasTowVehicle === true && !!towVehicle.year && !!towVehicle.make && !!towVehicle.model && !!towVehicle.capacity));

    const tradeDone =
      hasTrade === false ||
      (hasTrade === true && !!tradeInfo.year && !!tradeInfo.make && !!tradeInfo.model);

    const priorityDone = priorityFeatures.length > 0;

    // "Tow Vehicle Check" free-tool entry: when the hero CTA sets
    // towFirstMode, we bypass the progressive gate and reveal ONLY
    // the Tow section. The other sections (Contact, RV Type, Length,
    // Budget, Camping) are hidden until the Tow lookup returns a
    // usable year/make/model — at which point they cascade in via the
    // normal progressive gate. This makes the Tow Vehicle Check feel
    // like a standalone tool that gracefully evolves into the full
    // form after the payoff.
    const towLookupComplete = !!(towVehicle.year && towVehicle.make && towVehicle.model);
    const hidePreTowSections = towFirstMode && !towLookupComplete;

    return {
      contact: !hidePreTowSections,
      rvType: !hidePreTowSections,
      rvLength: !hidePreTowSections && contactDone && rvTypeDone,
      budget: !hidePreTowSections && contactDone && rvTypeDone && rvLengthDone,
      camping: !hidePreTowSections && contactDone && rvTypeDone && rvLengthDone && budgetDone && financingFullDone,
      // Tow: normal gate OR forced-open by the hero "Tow Vehicle Check" CTA.
      tow: towFirstMode || (contactDone && rvTypeDone && rvLengthDone && budgetDone && financingFullDone && travelDone),
      trade: contactDone && rvTypeDone && rvLengthDone && budgetDone && financingFullDone && travelDone && towDone,
      priority: contactDone && rvTypeDone && rvLengthDone && budgetDone && financingFullDone && travelDone && towDone && tradeDone,
      mustHaves: contactDone && rvTypeDone && rvLengthDone && budgetDone && financingFullDone && travelDone && towDone && tradeDone && priorityDone,
      notes: contactDone && rvTypeDone && rvLengthDone && budgetDone && financingFullDone && travelDone && towDone && tradeDone && priorityDone && mustHaves.length > 0,
    };
  }, [
    firstName, lastName, email, phone, zipcode,
    rvType, rvLength, budget, financing, monthlyPayment, downPayment, credit, preApproved,
    sleeps, campingStyle, tripType, hasKids, hasPets,
    isTowable, hasTowVehicle, towVehicle, towFirstMode,
    hasTrade, tradeInfo,
    priorityFeatures, mustHaves,
  ]);

  // ---------- Vehicle lookup (VIN or year/make/model → tow capacity + payload) ----------
  const handleVehicleLookup = async ({ vin, ymm } = {}) => {
    setVehicleLookupStatus({ loading: true, source: null, confidence: null, notes: null });
    try {
      const payload = vin
        ? { vin }
        : { year: towVehicle.year, make: towVehicle.make, model: towVehicle.model };
      const res = await axios.post(`${API}/vehicle/lookup`, payload);
      const d = res.data || {};
      setTowVehicle(p => ({
        ...p,
        year: d.year ? String(d.year) : p.year,
        make: d.make || p.make,
        model: d.model || p.model,
        capacity: d.tow_capacity_lbs ? String(d.tow_capacity_lbs) : p.capacity,
        payload: d.payload_lbs ? String(d.payload_lbs) : p.payload,
      }));
      setVehicleLookupStatus({
        loading: false,
        source: d.source,
        confidence: d.confidence,
        notes: d.notes,
        engine: d.engine || null,
        towRange: d.tow_capacity_range || null,
        gcwr: d.gcwr_lbs || null,
        driveType: d.drive_type || null,
      });
      toast.success(
        d.tow_capacity_lbs
          ? `Found: ${d.year || ''} ${d.make || ''} ${d.model || ''} — tow ~${d.tow_capacity_lbs} lbs`
          : 'Vehicle decoded — please double-check the values'
      );
    } catch (err) {
      console.error(err);
      setVehicleLookupStatus({ loading: false, source: null, confidence: null, notes: null });
      toast.error(err.response?.data?.detail || 'Could not look up the vehicle. Try entering specs manually.');
    }
  };

  // ---------- Door-jamb sticker upload → OCR → VIN → spec pipeline ----------
  const handleStickerUpload = async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Image is too large — please pick something under 12 MB.');
      return;
    }
    setStickerBusy(true);
    setVehicleLookupStatus({ loading: true, source: null, confidence: null, notes: null });
    try {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result || ''));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const res = await axios.post(`${API}/vehicle/lookup-sticker`, { image_base64: b64 });
      const d = res.data || {};
      setTowVehicle(p => ({
        ...p,
        vin: d.vin || p.vin,
        year: d.year ? String(d.year) : p.year,
        make: d.make || p.make,
        model: d.model || p.model,
        capacity: d.tow_capacity_lbs ? String(d.tow_capacity_lbs) : p.capacity,
        payload: d.payload_lbs ? String(d.payload_lbs) : p.payload,
      }));
      setVehicleLookupStatus({
        loading: false,
        source: d.source,
        confidence: d.confidence,
        notes: d.notes,
        engine: d.engine || null,
        towRange: d.tow_capacity_range || null,
        gcwr: d.gcwr_lbs || null,
        driveType: d.drive_type || null,
      });
      if (d.vin && d.tow_capacity_lbs) {
        toast.success(`Sticker OCR worked — ${d.year || ''} ${d.make || ''} ${d.model || ''}, tow ~${d.tow_capacity_lbs} lbs`);
      } else if (d.vin) {
        toast.success(`Sticker OCR read VIN ${d.vin}`);
      } else {
        toast.success('Sticker scanned — see the note below.');
      }
    } catch (err) {
      setVehicleLookupStatus({ loading: false, source: null, confidence: null, notes: null });
      toast.error(err.response?.data?.detail || 'Could not read that photo. Try a sharper, well-lit shot.');
    } finally {
      setStickerBusy(false);
      // Let the same file be reselected if the user retries
      try { event.target.value = ''; } catch { /* noop */ }
    }
  };

  // Auto-lookup tow specs when year/make/model are all populated and we don't have a capacity yet
  const maybeLookupYMM = () => {
    if (
      hasTowVehicle === true &&
      towVehicle.year && towVehicle.make && towVehicle.model &&
      !towVehicle.capacity &&
      !vehicleLookupStatus.loading
    ) {
      handleVehicleLookup({ ymm: true });
    }
  };

  const isFakePhone = (digits) => {
    const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (d.length !== 10) return true;
    if (/^(\d)\1{9}$/.test(d)) return true; // 0000000000, 1111111111 …
    if (d === '1234567890' || d === '0123456789' || d === '9876543210') return true;
    if (/^(\d)\1{2}(\d)\2{2}(\d)\3{3}$/.test(d)) return true; // 111-222-3333 style
    const area = d.slice(0, 3);
    if (area[0] === '0' || area[0] === '1' || area === '555') return true; // invalid NANP area codes / fake 555
    if (d.slice(3, 6) === '555') return true; // 555 exchange is fictional (e.g. 603-555-1234)
    return false;
  };

  const validate = () => {
    if (!firstName.trim()) return 'Please enter your first name';
    if (!lastName.trim()) return 'Please enter your last name';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email';
    const phoneDigits = phone.replace(/\D/g, '');
    if (!phone.trim() || phoneDigits.length < 10) return 'Please enter a valid phone number';
    if (isFakePhone(phoneDigits)) return 'That phone number doesn\'t look right — please enter a real number so your advisor can reach you';
    if (!zipcode.trim() || zipcode.length < 5) return 'Please enter a valid ZIP code';
    if (!rvType) return 'Please select an RV type';
    if (!rvLength) return 'Please select an RV length';
    if (!budget) return 'Please select a budget range';
    if (!financing) return 'Please tell us how you plan to pay';
    if (financing === 'finance') {
      if (!monthlyPayment) return 'Please pick a comfortable monthly payment';
      if (!downPayment) return 'Please tell us your down payment';
      if (!credit) return 'Please pick your credit profile';
      if (preApproved === null) return 'Please tell us if you\'re pre-approved';
    }
    if (!sleeps) return 'Please pick sleeping capacity';
    if (!campingStyle) return 'Please pick your camping style';
    if (!tripType) return 'Please pick your trip length';
    if (hasKids === null) return 'Please tell us if you travel with kids';
    if (hasPets === null) return 'Please tell us if you travel with pets';
    if (isTowable && hasTowVehicle === null) return 'Please tell us about your tow vehicle';
    if (isTowable && hasTowVehicle === true) {
      if (!towVehicle.year || !towVehicle.make || !towVehicle.model || !towVehicle.capacity) {
        return 'Please complete the tow vehicle details (or look up by VIN)';
      }
    }
    if (hasTrade === null) return 'Please tell us if you have a trade-in';
    if (hasTrade === true && (!tradeInfo.year || !tradeInfo.make || !tradeInfo.model)) {
      return 'Please complete the trade-in details (year/make/model)';
    }
    if (priorityFeatures.length === 0) return 'Please pick at least one priority feature';
    if (mustHaves.length === 0) return 'Please pick at least one must-have feature';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      // Scroll to top on validation error so user sees what's missing
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    try {
      const data = {
        first_name: firstName,
        last_name: lastName,
        name: `${firstName} ${lastName}`,
        email,
        phone: phone.replace(/\D/g, ''),
        zipcode,
        dealer_slug: effectiveDealerSlug || dealerSlug,
        matching_criteria: {
          rv_type: rvType,
          toy_hauler_style: rvType === 'toy_hauler' ? (toyHaulerStyle || 'either') : null,
          rv_length: rvLength,
          length_max: RV_LENGTH_RANGES.find(l => l.id === rvLength)?.max ?? 0,
          budget_max: BUDGET_RANGES.find(b => b.id === budget)?.value || 50000,
          sleep_capacity: parseInt(sleeps) || 2,
          camping_style: campingStyle || 'both',
          trip_type: tripType || null,
          has_kids: hasKids,
          has_pets: hasPets,
          must_have_features: mustHaves,
          floor_plans: floorPlans,
          condition_preference: 'either',
        },
        tow_vehicle: hasTowVehicle && towVehicle.make ? {
          year: parseInt(towVehicle.year) || null,
          make: towVehicle.make,
          model: towVehicle.model,
          tow_capacity: towVehicle.capacity || null,
          tow_capacity_source: towVehicle.capacitySource || null,
          payload: towVehicle.payload || null,
          payload_source: towVehicle.payloadSource || null,
          gvwr: towVehicle.gvwr || null,
          gvwr_source: towVehicle.gvwrSource || null,
          vin: towVehicle.vin || null,
        } : null,
        user_responses: {
          'RV Type': RV_TYPES.find(t => t.id === rvType)?.label,
          'RV Length': RV_LENGTH_RANGES.find(l => l.id === rvLength)?.label,
          'Budget Range': BUDGET_RANGES.find(b => b.id === budget)?.label,
          'Sleeping Capacity': SLEEP_OPTIONS.find(s => s.id === sleeps)?.label,
          'Camping Style': CAMPING_STYLES.find(c => c.id === campingStyle)?.label || '—',
          'Trip Type': TRIP_TYPES.find(t => t.id === tripType)?.label || '—',
          'Has Kids': hasKids === null ? '—' : hasKids ? 'Yes' : 'No',
          'Has Pets': hasPets === null ? '—' : hasPets ? 'Yes' : 'No',
          'Financing Plan': FINANCING_OPTIONS.find(f => f.id === financing)?.label || '—',
          'Monthly Payment Range': MONTHLY_PAYMENT_RANGES.find(m => m.id === monthlyPayment)?.label || '—',
          'Down Payment': DOWN_PAYMENT_RANGES.find(d => d.id === downPayment)?.label || '—',
          'Credit Profile': CREDIT_OPTIONS.find(c => c.id === credit)?.label || '—',
          'Pre-Approved': preApproved === null ? '—' : preApproved ? 'Yes' : 'No',
          'Has Trade-In': hasTrade === null ? '—' : hasTrade ? 'Yes' : 'No',
          'Trade Info': hasTrade
            ? `${tradeInfo.year || ''} ${tradeInfo.make || ''} ${tradeInfo.model || ''}`.trim() || '—'
            : '—',
          'Trade Monthly Payment': hasTrade ? (tradeInfo.monthlyPayment || '—') : '—',
          'Trade Lienholder': hasTrade ? (tradeInfo.lienholder || '—') : '—',
          'Trade Notes': hasTrade ? (tradeInfo.notes || '—') : '—',
          'Priority Features': priorityFeatures.map(f => PRIORITY_FEATURES.find(pf => pf.id === f)?.label).join(', ') || '—',
          'Must-Have RV Features': mustHaves.map(f => MUST_HAVE_FEATURES.find(mf => mf.id === f)?.label).join(', ') || '—',
          'Floor Plan Preferences': floorPlans.map(f => FLOOR_PLAN_OPTIONS.find(fp => fp.id === f)?.label).join(', ') || '—',
          // Tow vehicle — critical for trailer matches (weight rating) and
          // was missing from the profile view before. Only include when the
          // buyer said they have one.
          'Has Tow Vehicle': hasTowVehicle === null ? '—' : hasTowVehicle ? 'Yes' : 'No',
          'Tow Vehicle': hasTowVehicle
            ? [towVehicle.year, towVehicle.make, towVehicle.model, towVehicle.trim]
                .filter(Boolean).join(' ').trim() || '—'
            : '—',
          'Tow Vehicle VIN': hasTowVehicle ? (towVehicle.vin || '—') : '—',
          // Additional notes — the buyer's free-text "anything else" pasted
          // at the bottom of the form. Include so dealers see it inline.
          'Additional Notes': additionalNotes?.trim() || '—',
        },
        additional_notes: additionalNotes,
        // Capture refer-a-friend ?ref=ABCD1234 from the URL or localStorage so
        // the new lead inherits referred_by — the friend gets $50 off VIP
        // automatically at /api/vip/create-payment time.
        referred_by: (() => {
          try {
            const url = new URLSearchParams(window.location.search);
            const fromUrl = (url.get('ref') || '').trim();
            if (fromUrl) {
              try { localStorage.setItem('rvmatch_referrer_ref', fromUrl.toUpperCase()); } catch { /* noop */ }
              return fromUrl.toUpperCase();
            }
            const stored = localStorage.getItem('rvmatch_referrer_ref');
            return stored ? stored.toUpperCase() : null;
          } catch { return null; }
        })(),
        // Ad attribution — gclid/utm_* captured on /rigready (sessionStorage)
        // or present on this page's URL. Allow-listed keys only, no PII.
        attribution: (() => {
          try {
            const keys = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'referrer'];
            const raw = {};
            try { Object.assign(raw, JSON.parse(sessionStorage.getItem('rigready.attrib') || '{}')); } catch { /* noop */ }
            const q = new URLSearchParams(window.location.search);
            keys.forEach((k) => { const v = q.get(k); if (v) raw[k] = v; });
            const clean = {};
            keys.forEach((k) => { if (raw[k]) clean[k] = String(raw[k]).slice(0, 300); });
            return Object.keys(clean).length ? clean : null;
          } catch { return null; }
        })(),
        // Anonymous Rig Ready funnel session — links pre-lead TowIQ activity
        rigready_anon_id: (() => {
          try { return localStorage.getItem('rigready.anon_id') || null; } catch { return null; }
        })(),
      };

      const res = await axios.post(`${API}/quiz/application-submit`, data);
      if (res.data.success && res.data.session_id) {
        // Google Ads / GA conversion events (consumed by GTM/gtag if present)
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'rvmatch_complete' });
          window.dataLayer.push({ event: 'lead_submitted' });
          // Google Ads primary lead conversion — fires ONLY after the backend
          // confirmed lead creation, and ONLY once a real conversion label is
          // configured (REACT_APP_GADS_CONVERSION_LABEL). Never guessed.
          const gadsLabel = process.env.REACT_APP_GADS_CONVERSION_LABEL;
          if (gadsLabel && typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', { send_to: `AW-18458878897/${gadsLabel}` });
          }
        } catch { /* noop */ }
        // Clear the autosave draft so a fresh visit starts clean
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }

        // Upload any attachments the user picked. Best-effort: a failed
        // upload doesn't block the flow — we just toast a warning and keep
        // going so the user reaches their matches.
        if (pendingAttachments.length > 0) {
          toast.info(`Uploading ${pendingAttachments.length} file${pendingAttachments.length > 1 ? 's' : ''}…`);
          for (const f of pendingAttachments) {
            try {
              const fd = new FormData();
              fd.append('file', f);
              await axios.post(`${API}/leads/${res.data.session_id}/attachments`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 60000,
              });
            } catch (e) {
              console.error('Attachment upload failed:', f.name, e);
              toast.warning(`Couldn't upload "${f.name}" — you can re-add it from your dashboard later.`);
            }
          }
        }

        toast.success('Got it! Unlocking your matches…');
        // Send users to the 10-minute tool-bundle discount gate first;
        // the ?welcome=1 flag is forwarded through so the eventual matches
        // dashboard triggers the celebratory reveal on arrival.
        navigate(`/tools-discount/${res.data.session_id}?welcome=1`);
      } else {
        throw new Error(res.data.message || 'Submission failed');
      }
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070d]" data-testid="landing-page">

      {/* Top tools rail removed — tools are now featured inline as a
          dedicated section below the hero (see "PREMIUM TOOLS"). */}

      {/* ============================================================
          CINEMATIC HERO — looped Ken-Burns crossfade through 3 cinematic
          RV-at-sunrise stills. Uses real <img> tags (instead of CSS
          background-image) for maximum mobile-Safari reliability — some
          older iOS versions skip background-image rendering inside
          absolutely-positioned divs during a paint cycle.
          Hidden in embed mode (partner sites have their own header).
          ============================================================ */}
      {!embedMode && (
      <section className="relative isolate overflow-hidden bg-[#05070d] text-white min-h-[640px] sm:min-h-[700px]">
        {/* 3-image Ken-Burns slideshow (real img tags) */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
          {['/hero/hero-1.jpg', '/hero/hero-2.jpg', '/hero/hero-3.jpg'].map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'auto'}
              decoding="async"
              className="hero-kenburns absolute inset-0 w-full h-full object-cover"
              style={{ animationDelay: `${i * 6.5}s` }}
            />
          ))}
        </div>
        {/* Dark gradient overlay → keeps headline crisp against any frame */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#05070d]/55 via-[#05070d]/60 to-[#05070d]" />
        {/* Floating gradient orbs */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-[#0ea5e9] opacity-20 blur-[110px] animate-pulse-slow" />
          <div className="absolute top-32 -right-32 w-[32rem] h-[32rem] rounded-full bg-[#4f46e5] opacity-25 blur-[120px] animate-pulse-slow" style={{animationDelay:'2s'}} />
          <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] rounded-full bg-[#06b6d4] opacity-15 blur-[120px] animate-pulse-slow" style={{animationDelay:'4s'}} />
          <div className="absolute inset-0 opacity-[0.07]" style={{backgroundImage:'linear-gradient(rgba(255,255,255,.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.4) 1px, transparent 1px)', backgroundSize:'48px 48px'}} />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-24 md:pt-24 md:pb-32 text-center">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm text-xs font-bold tracking-[0.25em] text-cyan-200">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            FIND THE RV THAT ACTUALLY FITS
          </div>

          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tight leading-[1.02] mb-5">
            <span className="block">Find Your Right RV—</span>
            <span className="block bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-sky-400 to-indigo-400">
              Before You Make the Wrong Purchase.
            </span>
          </h1>
          <p className="max-w-2xl mx-auto text-base sm:text-lg text-slate-300 leading-relaxed mb-9">
            Answer a few questions about how you travel, who comes with you, your tow vehicle and your budget. RV Match AI gives you personalized recommendations, explains the tradeoffs and helps you take the next step with confidence.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center">
            <a
              href="#start-application"
              data-testid="hero-cta-primary"
              onClick={() => {
                // "Find My RV" always drops the user into the standard
                // full-form flow. If they'd previously clicked Tow
                // Vehicle Check, exit that mode explicitly so the form
                // renders Contact + RV Type + everything else instead
                // of the tow-only card.
                try { window.sessionStorage.removeItem('rvm.startAtTow'); } catch { /* noop */ }
                setTowFirstMode(false);
                window.dispatchEvent(new CustomEvent('rvm:analytics', { detail: { event: 'hero_free_match_clicked' } }));
              }}
              className="group relative inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl text-base font-bold text-[#05070d] bg-gradient-to-r from-cyan-300 via-sky-400 to-indigo-400 shadow-[0_0_40px_-8px_rgba(56,189,248,0.6)] hover:shadow-[0_0_60px_-4px_rgba(56,189,248,0.85)] transition-shadow"
            >
              <span className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-300 via-sky-400 to-indigo-400 blur-xl opacity-50 group-hover:opacity-80 transition-opacity -z-10" />
              <Sparkles className="w-5 h-5" />
              Find My RV — Free
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={effectiveDealerSlug ? `/rigready?advisor=${encodeURIComponent(effectiveDealerSlug)}` : '/rigready'}
              data-testid="hero-cta-tow-check"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('rvm:analytics', { detail: { event: 'tow_check_started' } }));
              }}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl text-base font-semibold text-white bg-gradient-to-r from-[#004E86]/70 to-[#004E86] hover:from-[#004E86] hover:to-[#003d6b] border border-white/15 backdrop-blur-sm transition-colors"
              title="Check your truck's tow rating from VIN or a photo of the door-jamb sticker — no account needed."
            >
              <Truck className="w-4 h-4" />
              Rig Ready TowIQ →
            </a>
          </div>
          <p className="mt-5 text-xs sm:text-sm text-slate-400">
            Free to use · No account required to start · No paid placement in your matches
          </p>
          {/* Install-as-app pill — mobile only. */}
          <button
            type="button"
            onClick={() => window.__rvOpenInstallPrompt && window.__rvOpenInstallPrompt()}
            data-testid="hero-install-app-btn"
            className="md:hidden mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#F2A900] to-[#E76F51] shadow-lg hover:shadow-xl active:scale-95 transition-all"
          >
            <Download className="w-4 h-4" />
            Add to Home Screen
          </button>
        </div>
      </section>
      )}
      {/* End of hero (hidden in embed mode) */}

      {/* ============================================================
          HOW IT WORKS — 3-step journey immediately below hero.
          Anchor target for the "See How It Works" CTA.
          ============================================================ */}
      {!embedMode && (
      <section
        id="how-it-works"
        className="relative bg-[#05070d] text-white py-16 md:py-20 border-t border-white/5"
        data-testid="how-it-works"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-[0.25em] text-cyan-300 mb-2">HOW IT WORKS</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">A clearer path from question to keys.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                id: 'match',
                num: '01',
                title: 'Match for Free',
                body: 'Tell us how you plan to camp and receive personalized RV recommendations.',
                cta: { label: 'Start Free Match', href: '#start-application', event: 'hero_free_match_clicked' },
                accent: 'from-cyan-500/20 to-cyan-500/0 border-cyan-400/30',
                ctaCls: 'bg-cyan-400 text-[#05070d] hover:bg-cyan-300',
              },
              {
                id: 'buy',
                num: '02',
                title: 'Buy with Clarity',
                body: 'Use RV Match tools to compare RVs, estimate ownership costs, analyze any sale offer and prepare your negotiation.',
                cta: { label: 'Explore Buyer Tools', href: '#product-ladder', event: 'buyer_tools_clicked' },
                accent: 'from-amber-500/20 to-amber-500/0 border-amber-400/30',
                ctaCls: 'bg-amber-400 text-[#05070d] hover:bg-amber-300',
              },
              {
                id: 'help',
                num: '03',
                title: 'Get Personal Help',
                body: 'Upgrade to VIP for personalized review, tow validation, buying strategy and direct guidance.',
                cta: { label: 'View VIP Options', href: '/checkout/vip_buying_plan', event: 'vip_options_clicked' },
                accent: 'from-violet-500/20 to-violet-500/0 border-violet-400/30',
                ctaCls: 'bg-violet-400 text-[#05070d] hover:bg-violet-300',
              },
            ].map((step) => (
              <div
                key={step.id}
                className={`rounded-2xl border bg-gradient-to-br ${step.accent} bg-white/[0.02] p-5 sm:p-6 flex flex-col`}
                data-testid={`journey-step-${step.id}`}
              >
                <div className="text-xs font-black tracking-[0.25em] text-white/40 mb-3">{step.num}</div>
                <h3 className="text-xl sm:text-2xl font-black mb-2">{step.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed mb-5 flex-1">{step.body}</p>
                <a
                  href={step.cta.href}
                  onClick={() => window.dispatchEvent(new CustomEvent('rvm:analytics', { detail: { event: step.cta.event } }))}
                  className={`inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-bold transition-colors ${step.ctaCls}`}
                  data-testid={`journey-cta-${step.id}`}
                >
                  {step.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* All marketing sections (AI mock, brand showcase, premium tools,
          ebook teasers, testimonials, how-it-works) are hidden in embed
          mode so partner sites get just the conversion form. */}
      {!embedMode && (<>

      {/* ============================================================
          LIVE AI MATCH EXPERIENCE — animated mock that cycles through
          input → analyzing → 3 result cards reveal.
          ============================================================ */}
      <section
        data-testid="hero-match-preview"
        className="relative bg-[#05070d] pt-2 pb-16 md:pb-20 border-t border-white/5"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 md:mb-8">
            <p className="text-xs font-bold tracking-[0.25em] text-cyan-300 mb-2">A PEEK AT YOUR RESULTS</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white tracking-tight">Three cards. One clear next step.</h2>
            <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-xl mx-auto">
              Every match tells you why it fits, what it's best for, and the one honest tradeoff to weigh — so you never buy blind.
            </p>
          </div>
          <MatchPreviewCards />
        </div>
      </section>

      {/* ============================================================
          BRAND SHOWCASE — premium product-box hero. Mirrors the
          packaging the founders put behind the brand: AI-Powered
          Matching, Smart Preferences, Trusted Recommendations,
          Adventure Awaits. Static image + matching typed copy so
          screen readers and SEO get the same value as the visual.
          ============================================================ */}
      {/* ============================================================
          BUYER BENEFITS — replaces the old brand-showcase software box.
          Focuses on what the buyer gets, not on generic AI claims. Every
          claim is factually true for the current matching pipeline.
          ============================================================ */}
      <section
        data-testid="buyer-benefits-section"
        className="relative bg-[#05070d] text-white py-20 md:py-24 overflow-hidden border-t border-white/5"
      >
        <div className="pointer-events-none absolute inset-0 opacity-25">
          <div className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-sky-500 blur-[140px]" />
          <div className="absolute -bottom-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-blue-600 blur-[140px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-[0.25em] text-sky-400 mb-3">WHY IT WORKS</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight">Made for real buyers making a real decision.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {[
              {
                icon: Search,
                title: 'Personalized Matches',
                body: 'Recommendations based on your travel style, sleeping needs, floorplan preferences, tow vehicle and budget.',
                color: 'sky',
              },
              {
                icon: ListChecks,
                title: 'Real Tradeoffs',
                body: 'Understand why each RV fits — and what compromises to consider before visiting the lot.',
                color: 'cyan',
              },
              {
                icon: ShieldCheck,
                title: 'Tow-Aware Guidance',
                body: 'Include tow compatibility when relevant, and clearly identify when vehicle info still needs verification.',
                color: 'indigo',
              },
              {
                icon: MapPin,
                title: 'Your Next Best Step',
                body: 'Save your matches, compare finalists, analyze a real deal or get personalized VIP buying help.',
                color: 'blue',
              },
            ].map(({ icon: Icon, title, body, color }) => {
              const ringMap = {
                sky: 'border-sky-400/40 bg-sky-400/10 text-sky-300',
                blue: 'border-blue-400/40 bg-blue-400/10 text-blue-300',
                cyan: 'border-cyan-400/40 bg-cyan-400/10 text-cyan-300',
                indigo: 'border-indigo-400/40 bg-indigo-400/10 text-indigo-300',
              }[color];
              return (
                <div
                  key={title}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 sm:p-6"
                  data-testid={`benefit-${title.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className={`w-11 h-11 rounded-xl border flex items-center justify-center mb-3 ${ringMap}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">{title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          PREMIUM TOOLS — 7-product ladder (see /components/ProductLadder).
          The free RV Match quiz stays above the fold; this section shows
          the paid upgrade path: 4 individual tools → $59.99 bundle
          (Most Popular) → VIP Buying Plan → VIP Concierge.
          ============================================================ */}
      <ProductLadder />

      {/* ============================================================
          FEATURES — 6 glassmorphism cards w/ hover glow
          ============================================================ */}
      <section className="relative bg-[#05070d] text-white py-20 md:py-24 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-0 w-96 h-96 rounded-full bg-indigo-500 blur-[140px]" />
          <div className="absolute bottom-1/4 right-0 w-96 h-96 rounded-full bg-cyan-500 blur-[140px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-xs font-bold tracking-[0.25em] text-cyan-400 mb-3">PLATFORM CAPABILITIES</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight">Everything you need to buy smart.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { icon: Sparkles, color: 'cyan', title: 'AI-Powered Matching', desc: 'Advanced algorithms match buyers with RVs that truly fit their lifestyle.' },
              { icon: Truck, color: 'sky', title: 'Tow Vehicle Validation', desc: 'Instantly know what your vehicle can safely tow.' },
              { icon: Wallet, color: 'emerald', title: 'Real Ownership Insights', desc: 'Discover hidden costs, storage expectations, maintenance realities, and depreciation.' },
              { icon: BarChart3, color: 'indigo', title: 'Advisor Dashboard', desc: 'Help RV advisors qualify leads and match inventory faster.' },
              { icon: Calculator, color: 'rose', title: 'Smart Comparison Engine', desc: 'Compare RVs side-by-side using real ownership logic.' },
              { icon: Compass, color: 'amber', title: 'Adventure Planning', desc: 'Get recommendations based on how YOU actually travel.' },
            ].map((f, i) => {
              const Icon = f.icon;
              const colorMap = {
                cyan: 'from-cyan-500/20 to-cyan-500/0 group-hover:border-cyan-400/50 text-cyan-300',
                sky: 'from-sky-500/20 to-sky-500/0 group-hover:border-sky-400/50 text-sky-300',
                emerald: 'from-emerald-500/20 to-emerald-500/0 group-hover:border-emerald-400/50 text-emerald-300',
                indigo: 'from-indigo-500/20 to-indigo-500/0 group-hover:border-indigo-400/50 text-indigo-300',
                rose: 'from-rose-500/20 to-rose-500/0 group-hover:border-rose-400/50 text-rose-300',
                amber: 'from-amber-500/20 to-amber-500/0 group-hover:border-amber-400/50 text-amber-300',
              }[f.color];
              return (
                <div
                  key={i}
                  className={`group relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 transition-all duration-300 hover:bg-white/[0.06] hover:-translate-y-1`}
                  data-testid={`feature-card-${i}`}
                >
                  <div className={`mb-4 w-11 h-11 rounded-xl bg-gradient-to-br ${colorMap} border border-white/10 flex items-center justify-center transition-colors`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          DEALER DASHBOARD PREVIEW
          ============================================================ */}
      <section className="relative bg-gradient-to-b from-[#05070d] via-[#070b16] to-[#05070d] text-white py-20 md:py-28 overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-xs font-bold tracking-[0.25em] text-indigo-300 mb-3">FOR DEALERSHIPS</p>
              <h2 className="text-3xl sm:text-5xl font-black tracking-tight mb-5 leading-tight">
                The Future Of <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-indigo-400">RV Sales Intelligence</span>
              </h2>
              <p className="text-base text-slate-300 mb-6 leading-relaxed">
                Built for RV advisors that want smarter lead qualification, better customer experiences, and faster closings.
              </p>
              <ul className="space-y-3 text-sm text-slate-300 mb-8">
                {['Live qualified-lead feed with intent scoring', 'AI-matched inventory suggestions per customer', 'Auto-generated talking points for every lead', 'Close-probability prediction & follow-up automation'].map((line, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Check className="w-5 h-5 text-cyan-400 mt-0.5 shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <a
                href="/dealer-plans"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold bg-white text-[#05070d] hover:bg-slate-100 transition-colors"
                data-testid="dealer-access-cta"
              >
                Advisor Access <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="order-1 lg:order-2">
              <DealerDashboardMock />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          PREMIUM RESOURCES — teaser cards for the two flagship ebooks.
          Intentionally vague about access. CTA scrolls to the form so
          curiosity converts directly into applications.
          ============================================================ */}
      <section
        data-testid="premium-resources-section"
        className="relative bg-[#05070d] text-white py-20 md:py-24 overflow-hidden border-t border-white/5"
      >
        <div className="pointer-events-none absolute inset-0 opacity-25">
          <div className="absolute -top-24 right-1/4 w-[28rem] h-[28rem] rounded-full bg-amber-500 blur-[140px]" />
          <div className="absolute -bottom-24 left-1/4 w-[28rem] h-[28rem] rounded-full bg-emerald-500 blur-[140px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-14">
            <p className="text-xs font-bold tracking-[0.25em] text-amber-400 mb-3">FREE WITH YOUR APPLICATION</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight">
              The playbooks that <span className="text-amber-400">save buyers thousands.</span>
            </h2>
            <p className="text-slate-400 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              Two field-tested guides — yours <span className="text-emerald-400 font-bold">free</span> the moment you complete the application. Buy smarter. Travel smarter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {[
              {
                id: 'buying-guide',
                eyebrow: 'Buying Playbook',
                title: 'The Smart Buyer\u2019s RV Buying Guide',
                tagline: 'Buy the right RV at the right price. Avoid the traps. Walk in confident, walk out paid.',
                bullets: [
                  'Match the RV class to your real lifestyle',
                  'The true 5\u201310 year cost of ownership',
                  'Financing without getting cooked',
                  'Advisor tactics + counter-scripts that work',
                ],
                meta: '9 chapters \u00B7 ~11 pages',
                gradient: 'from-blue-600/40 via-blue-500/10 to-transparent',
                ring: 'border-blue-400/30',
                accent: 'text-blue-300',
                badgeBg: 'bg-blue-500/15',
                badgeText: 'text-blue-300',
                bookSpine: 'from-blue-700 to-indigo-900',
              },
              {
                id: 'travel-guide',
                eyebrow: 'Travel Playbook',
                title: 'The RV Travel & Camping Guide',
                tagline: 'Plan trips that actually work. Find the campgrounds locals know. Travel longer for less.',
                bullets: [
                  'Routes, regions, and seasons that pay off',
                  'Campground tiers \u2014 free to luxury',
                  'Boondocking + off-grid power that lasts',
                  'The travel-day routine that prevents disasters',
                ],
                meta: '9 chapters \u00B7 ~11 pages',
                gradient: 'from-emerald-600/40 via-emerald-500/10 to-transparent',
                ring: 'border-emerald-400/30',
                accent: 'text-emerald-300',
                badgeBg: 'bg-emerald-500/15',
                badgeText: 'text-emerald-300',
                bookSpine: 'from-emerald-700 to-teal-900',
              },
            ].map((g) => (
              <div
                key={g.id}
                data-testid={`ebook-card-${g.id}`}
                className={`relative rounded-3xl border ${g.ring} bg-gradient-to-br ${g.gradient} backdrop-blur-sm p-6 sm:p-8 flex flex-col`}
              >
                {/* Free badge — these resources unlock free on application submit */}
                <div className={`absolute top-5 right-5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 text-[10px] font-bold tracking-widest uppercase backdrop-blur-sm`}>
                  ✓ Free
                </div>

                {/* Faux book cover */}
                <div className="flex items-stretch gap-5 mb-5">
                  <div className={`relative shrink-0 w-24 h-32 sm:w-28 sm:h-36 rounded-md bg-gradient-to-br ${g.bookSpine} shadow-[0_18px_45px_-15px_rgba(0,0,0,0.8)] ring-1 ring-white/10 overflow-hidden`}>
                    <div className="absolute inset-y-0 left-0 w-1.5 bg-black/40" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(255,255,255,0.18),transparent_55%)]" />
                    <div className="absolute inset-0 p-3 flex flex-col justify-between">
                      <BookOpen className="w-5 h-5 text-white/80" />
                      <div>
                        <p className="text-[8px] font-bold uppercase tracking-widest text-white/60">RV Match A.I.</p>
                        <p className="text-[10px] font-black leading-tight text-white mt-0.5">
                          {g.id === 'buying-guide' ? 'BUYING\nGUIDE' : 'TRAVEL\nGUIDE'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${g.accent}`}>{g.eyebrow}</p>
                    <h3 className="text-xl sm:text-2xl font-black leading-tight mb-2">{g.title}</h3>
                    <p className="text-sm text-slate-300 leading-relaxed">{g.tagline}</p>
                  </div>
                </div>

                {/* Chapter teaser */}
                <ul className="space-y-2 mb-5">
                  {g.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className={`w-4 h-4 ${g.accent} mt-0.5 shrink-0`} /> <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-white/10">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{g.meta}</p>
                  <a
                    href="#start-application"
                    data-testid={`ebook-cta-${g.id}`}
                    onClick={() => {
                      try { window.sessionStorage.removeItem('rvm.startAtTow'); } catch { /* noop */ }
                      setTowFirstMode(false);
                    }}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#F2A900] text-black text-sm font-bold hover:bg-amber-400 transition-colors"
                  >
                    Get my free copy <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 max-w-xl mx-auto">
            Complete the RV Match application below and both playbooks are instantly unlocked in your dashboard.
          </p>
        </div>
      </section>

      {/* ============================================================
          TESTIMONIALS
          ============================================================ */}
      <section className="bg-[#05070d] text-white py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.25em] text-cyan-400 mb-3">PEOPLE LOVE IT</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight">From buyers and advisors alike.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { quote: 'I wish this existed before we bought our first camper.', name: 'Sarah M.', title: 'First-time RV buyer' },
              { quote: 'This feels like having an RV expert and AI assistant combined.', name: 'James T.', title: 'Long-time camper' },
              { quote: 'Our dealership instantly improved lead quality.', name: 'Dealer Partner', title: 'RV Dealer Group' },
            ].map((t, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 hover:bg-white/[0.06] transition-colors">
                <div className="text-cyan-300 text-2xl leading-none mb-3">&ldquo;</div>
                <p className="text-base text-slate-200 leading-relaxed mb-5">{t.quote}</p>
                <div className="text-sm">
                  <p className="font-bold text-white">{t.name}</p>
                  <p className="text-slate-500 text-xs">{t.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          HOW IT WORKS — 3 steps with glowing connectors
          ============================================================ */}
      <section className="relative bg-[#05070d] text-white py-20 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[40rem] rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 blur-[160px]" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.25em] text-cyan-400 mb-3">HOW IT WORKS</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight">3 steps to your perfect match.</h2>
          </div>
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Glowing connector line on desktop */}
            <div aria-hidden="true" className="hidden md:block absolute top-12 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
            {[
              { n: '01', title: 'Tell Us About Your Lifestyle', desc: 'Family size, tow vehicle, camping style, budget — share what matters.' },
              { n: '02', title: 'AI Analyzes Compatibility', desc: 'Our engine evaluates thousands of RV configurations against your real-world fit.' },
              { n: '03', title: 'Get Your Perfect RV Match', desc: 'See your top 3 matches with cost, compatibility, and ownership insights.' },
            ].map((s, i) => (
              <div key={i} className="relative text-center">
                <div className="relative mx-auto w-24 h-24 rounded-2xl bg-gradient-to-br from-cyan-400/20 to-indigo-500/20 border border-white/15 flex items-center justify-center backdrop-blur-sm shadow-[0_0_40px_-12px_rgba(56,189,248,0.6)]">
                  <span className="text-3xl font-black bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-indigo-300">{s.n}</span>
                </div>
                <h3 className="text-xl font-bold mt-5 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-400 max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      </>)}
      {/* End of marketing-only sections (hidden in embed mode) */}

      {/* Anchor target — application form is the conversion section */}
      <div id="start-application" />

      {/* ============================================================
          APPLICATION FORM — cinematic conversion section.
          Same #05070d palette, ambient cyan/amber glow, glass section
          cards. Form internals already pass isDark which is locked to
          true at the page level, so they render with full dark contrast.
          ============================================================ */}
      <section className="relative bg-[#05070d] text-white overflow-hidden border-t border-white/5">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-30">
          <div className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-cyan-500 blur-[140px]" />
          <div className="absolute bottom-0 -right-24 w-[36rem] h-[36rem] rounded-full bg-amber-500 blur-[140px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 space-y-6 [&_input]:bg-white/[0.04] [&_input]:border-white/10 [&_input]:text-white [&_input::placeholder]:text-slate-500 [&_input:focus-visible]:ring-cyan-400 [&_textarea]:bg-white/[0.04] [&_textarea]:border-white/10 [&_textarea]:text-white [&_textarea::placeholder]:text-slate-500 [&_select]:bg-white/[0.04] [&_select]:border-white/10 [&_select]:text-white">
          <div className="text-center mb-4">
            <p className="text-xs font-bold tracking-[0.25em] text-cyan-400 mb-3">
              {towFirstMode ? 'RIG READY · TOWIQ' : 'YOUR APPLICATION'}
            </p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
              {towFirstMode
                ? (<>What can you <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-indigo-300">actually tow</span>?</>)
                : (<>Start your <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-indigo-300">RV match</span>.</>)
              }
            </h2>
            <p className="text-sm sm:text-base mt-3 text-slate-400 max-w-xl mx-auto">
              {towFirstMode
                ? 'Type your VIN or snap the door-jamb sticker — we\u2019ll pull the real tow rating from NHTSA. When you\u2019re done, the full RV match form opens right below.'
                : 'One page, ~3 minutes. The more you share, the sharper your matches.'}
            </p>
          </div>

        {/* Progress bar — hidden in tow-first mode until the payoff. */}
        {!towFirstMode ? (<FormProgress show={show} isTowable={isTowable} isDark={isDark} />) : null}

        {/* 1. Contact — hidden in tow-first mode until the tow lookup completes */}
        {show.contact && (
        <>
        <SectionCard icon={User} title="Contact Information" subtitle="So we can send your match results" isDark={isDark} accent="blue">
          {/* Refer-a-friend invite banner — only when ?ref= matches a real customer */}
          {referrerInfo && (
            <div className={`mb-5 p-4 rounded-2xl border-2 flex items-start gap-3 ${isDark ? 'border-emerald-700/50 bg-emerald-900/20' : 'border-emerald-300 bg-emerald-50'}`} data-testid="referrer-banner">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center font-bold">
                {(referrerInfo.first_name || 'F').slice(0,1)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                  You were invited
                </p>
                <p className={`text-sm sm:text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {referrerInfo.first_name}{referrerInfo.last_initial ? ` ${referrerInfo.last_initial}.` : ''} invited you — you'll get <span className="text-emerald-600 font-extrabold">$50 off VIP</span> ($147 instead of $197) at checkout.
                </p>
              </div>
            </div>
          )}

          {/* Returning-customer sign-in callout — stands out so past applicants can skip the form */}
          {!hideSignInPrompt && (
          <a
            href="/sign-in"
            data-testid="contact-signin-callout"
            className={`group relative flex items-center gap-3 mb-5 p-3.5 rounded-2xl border-2 border-dashed transition-all ${
              isDark
                ? 'border-[#F2A900]/50 bg-amber-500/10 hover:border-[#F2A900] hover:bg-amber-500/15'
                : 'border-[#F2A900] bg-amber-50 hover:bg-amber-100 hover:shadow-md'
            }`}
          >
            <div className="shrink-0 w-9 h-9 rounded-lg border-2 border-[#F2A900] bg-white flex items-center justify-center transition-colors group-hover:bg-[#F2A900]">
              {/* Empty checkbox — clickable to mark "I'm returning" */}
              <span className="w-4 h-4 rounded-sm border-2 border-[#F2A900] group-hover:border-black" />
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-[#F2A900]' : 'text-amber-700'}`}>
                Already applied before?
              </p>
              <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Check this and sign in with your email — skip the form, jump straight to your matches.
              </p>
            </div>
            <span className={`shrink-0 text-sm font-bold ${isDark ? 'text-[#F2A900]' : 'text-[#004E86]'} group-hover:translate-x-0.5 transition-transform`}>
              Sign in →
            </span>
          </a>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldWrap label="First Name *" isDark={isDark}>
              <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="John" data-testid="input-first-name" />
            </FieldWrap>
            <FieldWrap label="Last Name *" isDark={isDark}>
              <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Smith" data-testid="input-last-name" />
            </FieldWrap>
            <FieldWrap label="Email *" isDark={isDark} className="sm:col-span-2">
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} onBlur={handleEmailBlur} placeholder="john@example.com" data-testid="input-email" />
              {existingChecking && (
                <p className="text-xs text-gray-500 mt-1">Checking…</p>
              )}
              {existingApplicant && !existingDismissed && (
                <div className={`mt-2 p-3 rounded-xl border-2 flex items-start gap-3 ${isDark ? 'border-emerald-700/50 bg-emerald-900/20' : 'border-emerald-300 bg-emerald-50'}`} data-testid="welcome-back-banner">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">
                    {(existingApplicant.name || 'U').slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>Welcome back</p>
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      We already have matches for {existingApplicant.name || 'you'}. Skip the form?
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => { window.location.href = `/my-matches/${existingApplicant.latest_session_id}`; }}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold"
                        data-testid="welcome-back-go-btn"
                      >
                        View my matches →
                      </button>
                      <button
                        type="button"
                        onClick={() => { setExistingDismissed(true); setExistingApplicant(null); }}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'}`}
                        data-testid="welcome-back-dismiss-btn"
                      >
                        No, I want to retake the application
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </FieldWrap>
            <FieldWrap label="Phone *" isDark={isDark}>
              <Input type="tel" value={phone} onChange={e => setPhone(formatPhone(e.target.value))} placeholder="(555) 123-4567" data-testid="input-phone" />
            </FieldWrap>
            <FieldWrap label="ZIP Code *" isDark={isDark}>
              <Input value={zipcode} onChange={e => setZipcode(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="90210" maxLength={5} data-testid="input-zipcode" />
            </FieldWrap>
          </div>
        </SectionCard>

        {/* Optional file attachments — always visible from page load. Designed
            as a clearly-optional helper so it never blocks form progression.
            Files are uploaded after the lead is created (see handleSubmit). */}
        <div
          className={`rounded-2xl border ${isDark ? 'border-cyan-400/20 bg-cyan-500/[0.04]' : 'border-cyan-200 bg-cyan-50/60'} p-4 sm:p-5`}
          data-testid="attachments-card"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${isDark ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-400/30' : 'bg-white text-cyan-600'}`}>
              <Paperclip className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Got research already? <span className={isDark ? 'text-cyan-300' : 'text-cyan-700'}>(optional)</span>
              </p>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Attach notes, spreadsheets, or screenshots of RVs you've been eyeing — your advisor will see them. Skip if you don't have any.
              </p>
            </div>
          </div>

          <input
            id="attachments-input"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.md,.rtf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.webp,.heic,.heif,application/pdf,image/*,text/plain,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => {
              const incoming = Array.from(e.target.files || []);
              if (!incoming.length) return;
              const MAX = 5;
              const MAX_BYTES = 10 * 1024 * 1024;
              const accepted = [];
              for (const f of incoming) {
                if (pendingAttachments.length + accepted.length >= MAX) {
                  toast.warning(`You can attach up to ${MAX} files.`);
                  break;
                }
                if (f.size > MAX_BYTES) {
                  toast.warning(`"${f.name}" is over 10 MB and was skipped.`);
                  continue;
                }
                if (pendingAttachments.some(p => p.name === f.name && p.size === f.size)) continue;
                accepted.push(f);
              }
              if (accepted.length) setPendingAttachments(prev => [...prev, ...accepted]);
              e.target.value = '';
            }}
            data-testid="attachments-input"
          />
          <label
            htmlFor="attachments-input"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-colors text-sm font-semibold ${
              isDark
                ? 'border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20'
                : 'border-cyan-300 bg-white text-cyan-700 hover:bg-cyan-50'
            }`}
            data-testid="attachments-dropzone"
          >
            <Upload className="w-4 h-4" />
            {pendingAttachments.length === 0 ? 'Add files (optional)' : 'Add more files'}
          </label>

          {pendingAttachments.length > 0 && (
            <ul className="mt-4 space-y-2" data-testid="attachments-list">
              {pendingAttachments.map((f, idx) => (
                <li
                  key={`${f.name}-${idx}`}
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${
                    isDark ? 'bg-white/[0.04] border border-white/10' : 'bg-white border border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className={`w-4 h-4 shrink-0 ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`} />
                    <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.name}</span>
                    <span className={`text-xs shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {(f.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingAttachments(prev => prev.filter((_, i) => i !== idx))}
                    className={`shrink-0 p-1 rounded transition-colors ${
                      isDark ? 'text-slate-400 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label={`Remove ${f.name}`}
                    data-testid={`attachment-remove-${idx}`}
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        </>
        )}

        {/* 2. RV Type */}
        {show.rvType && (
        <SectionCard
          icon={Truck}
          title="What Type of RV?"
          subtitle="Pick the style that excites you most"
          isDark={isDark}
          accent="amber"
          eduTopic="rv_types"
          onEduOpen={openEdu}
          eduLabel="RV types 101"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {RV_TYPES.map(t => (
              <BoxOption key={t.id} active={rvType === t.id} onClick={() => setRvType(t.id)} isDark={isDark} testId={`rv-type-${t.id}`}>
                <div className="text-2xl mb-1">{t.icon}</div>
                <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.label}</div>
                <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.desc}</div>
              </BoxOption>
            ))}
          </div>
          {rvType === 'toy_hauler' && (
            <div className="mt-4" data-testid="toy-hauler-style-section">
              <p className={`text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Which toy hauler style?
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { id: 'travel_trailer', label: 'Travel Trailer Toy Hauler', desc: 'Bumper-pull hitch' },
                  { id: 'fifth_wheel', label: 'Fifth Wheel Toy Hauler', desc: 'In-bed hitch, needs truck' },
                  { id: 'either', label: 'Either / Not Sure', desc: "We'll match both styles" },
                ].map((s) => (
                  <BoxOption key={s.id} active={toyHaulerStyle === s.id} onClick={() => setToyHaulerStyle(s.id)} isDark={isDark} testId={`toy-hauler-style-${s.id}`}>
                    <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.label}</div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{s.desc}</div>
                  </BoxOption>
                ))}
              </div>
            </div>
          )}
        </SectionCard>
        )}

        {/* 2b. RV Length */}
        {show.rvLength && (
        <SectionCard icon={Ruler} title="RV Length" subtitle="Helps us match what fits your driveway, campsite, and tow rating" isDark={isDark} accent="blue">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {RV_LENGTH_RANGES.map(l => (
              <BoxOption key={l.id} active={rvLength === l.id} onClick={() => setRvLength(l.id)} isDark={isDark} testId={`rv-length-${l.id}`}>
                <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{l.label}</div>
              </BoxOption>
            ))}
          </div>
        </SectionCard>
        )}

        {/* 3. Budget & Financing */}
        {show.budget && (
        <SectionCard icon={DollarSign} title="Budget & Financing" subtitle="Helps us match RVs you can actually buy" isDark={isDark} accent="green">
          <div className="-mt-2 mb-4">
            <EduInfoButton topic="budget_financing" onOpen={openEdu} isDark={isDark} label="Budget & financing 101" />
          </div>
          <FieldLabel isDark={isDark}>Total Budget *</FieldLabel>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {BUDGET_RANGES.map(b => (
              <BoxOption key={b.id} active={budget === b.id} onClick={() => setBudget(b.id)} isDark={isDark} testId={`budget-${b.id}`}>
                <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{b.label}</div>
              </BoxOption>
            ))}
          </div>

          <FieldLabel isDark={isDark}>How are you planning to pay?</FieldLabel>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {FINANCING_OPTIONS.map(f => {
              const Icon = f.icon;
              return (
                <BoxOption key={f.id} active={financing === f.id} onClick={() => setFinancing(f.id)} isDark={isDark} testId={`financing-${f.id}`}>
                  <Icon className={`w-5 h-5 mb-2 ${financing === f.id ? 'text-[#F2A900]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.label}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{f.desc}</div>
                </BoxOption>
              );
            })}
          </div>

          {financing === 'finance' && (
            <>
              <FieldLabel isDark={isDark}>Comfortable monthly payment</FieldLabel>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {MONTHLY_PAYMENT_RANGES.map(m => (
                  <BoxOption key={m.id} active={monthlyPayment === m.id} onClick={() => setMonthlyPayment(m.id)} isDark={isDark} testId={`mp-${m.id}`}>
                    <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{m.label}</div>
                  </BoxOption>
                ))}
              </div>

              <FieldLabel isDark={isDark}>Down payment available</FieldLabel>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {DOWN_PAYMENT_RANGES.map(d => (
                  <BoxOption key={d.id} active={downPayment === d.id} onClick={() => setDownPayment(d.id)} isDark={isDark} testId={`dp-${d.id}`}>
                    <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.label}</div>
                  </BoxOption>
                ))}
              </div>

              <FieldLabel isDark={isDark}>Credit profile</FieldLabel>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {CREDIT_OPTIONS.map(c => (
                  <BoxOption key={c.id} active={credit === c.id} onClick={() => setCredit(c.id)} isDark={isDark} testId={`credit-${c.id}`}>
                    <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.label}</div>
                    {c.range && <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{c.range}</div>}
                  </BoxOption>
                ))}
              </div>

              <FieldLabel isDark={isDark}>Already pre-approved?</FieldLabel>
              <YesNoToggle value={preApproved} onChange={setPreApproved} isDark={isDark} testIdPrefix="preapproved" />
            </>
          )}
        </SectionCard>
        )}

        {/* 4. Travel & Camping */}
        {show.camping && (
        <SectionCard icon={Compass} title="Travel & Camping Style" subtitle="How you'll actually use it" isDark={isDark} accent="purple">
          <FieldLabel isDark={isDark}>Sleeping capacity *</FieldLabel>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {SLEEP_OPTIONS.map(s => (
              <BoxOption key={s.id} active={sleeps === s.id} onClick={() => setSleeps(s.id)} isDark={isDark} testId={`sleeps-${s.id}`}>
                <Users className={`w-5 h-5 mb-2 ${sleeps === s.id ? 'text-[#F2A900]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{s.label}</div>
              </BoxOption>
            ))}
          </div>

          <FieldLabel isDark={isDark}>Where will you camp?</FieldLabel>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {CAMPING_STYLES.map(c => {
              const Icon = c.icon;
              return (
                <BoxOption key={c.id} active={campingStyle === c.id} onClick={() => setCampingStyle(c.id)} isDark={isDark} testId={`camping-${c.id}`}>
                  <Icon className={`w-5 h-5 mb-2 ${campingStyle === c.id ? 'text-[#F2A900]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.label}</div>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{c.desc}</div>
                </BoxOption>
              );
            })}
          </div>

          <FieldLabel isDark={isDark}>Trip length</FieldLabel>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {TRIP_TYPES.map(t => (
              <BoxOption key={t.id} active={tripType === t.id} onClick={() => setTripType(t.id)} isDark={isDark} testId={`trip-${t.id}`}>
                <Calendar className={`w-5 h-5 mb-2 ${tripType === t.id ? 'text-[#F2A900]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.label}</div>
              </BoxOption>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FieldLabel isDark={isDark}>Traveling with kids?</FieldLabel>
              <YesNoToggle value={hasKids} onChange={setHasKids} isDark={isDark} testIdPrefix="kids" iconYes={Baby} />
            </div>
            <div>
              <FieldLabel isDark={isDark}>Traveling with pets?</FieldLabel>
              <YesNoToggle value={hasPets} onChange={setHasPets} isDark={isDark} testIdPrefix="pets" iconYes={PawPrint} />
            </div>
          </div>
        </SectionCard>
        )}

        {/* 5. Tow Vehicle (only if towable RV) */}
        {show.tow && isTowable && (
          <SectionCard
            icon={Car}
            title="Tow Vehicle"
            subtitle="So we only show RVs your truck can pull"
            isDark={isDark}
            accent="blue"
            eduTopic="tow_vehicle"
            onEduOpen={openEdu}
            eduLabel="Tow guide"
          >
            {/* Do-you-have-one toggle: hidden in tow-first mode since
                the user got here by clicking Rig Ready TowIQ (they
                obviously have one). Standard flow still shows it. */}
            {!towFirstMode && (
              <>
                <FieldLabel isDark={isDark}>Do you already have a tow vehicle?</FieldLabel>
                <YesNoToggle value={hasTowVehicle} onChange={setHasTowVehicle} isDark={isDark} testIdPrefix="tow" />
              </>
            )}

            {/* Secondary education pill for tow-weight glossary (GVWR, tongue weight, payload) */}
            <div className={`${towFirstMode ? '' : 'mt-3'} flex flex-wrap gap-2`}>
              <EduInfoButton topic="tow_weight" onOpen={openEdu} isDark={isDark} label="Tow weight explained" />
            </div>

            {hasTowVehicle && (
              <div className="space-y-4 mt-5">
                {/* VIN lookup */}
                <div className={`p-4 rounded-xl border ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-blue-50/50'}`}>
                  <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Have your VIN? Auto-fill the rest in seconds.
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={towVehicle.vin}
                      onChange={e => setTowVehicle(p => ({ ...p, vin: e.target.value.toUpperCase().slice(0, 17) }))}
                      placeholder="17-character VIN (e.g. 1FTFW1E84MFA12345)"
                      className="font-mono tracking-wider"
                      maxLength={17}
                      data-testid="tow-vin"
                    />
                    <Button
                      type="button"
                      onClick={() => handleVehicleLookup({ vin: towVehicle.vin })}
                      disabled={vehicleLookupStatus.loading || (towVehicle.vin || '').length !== 17}
                      className="bg-[#004E86] hover:bg-[#003d6b] text-white font-semibold whitespace-nowrap"
                      data-testid="tow-vin-lookup"
                    >
                      {vehicleLookupStatus.loading
                        ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" /> Searching</span>
                        : <span className="inline-flex items-center gap-1.5"><Search className="w-4 h-4" /> Look up VIN</span>}
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-2 flex-wrap mt-2">
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Or skip the VIN — we'll auto-look up tow specs once you fill year/make/model below.
                    </p>
                    <label
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold cursor-pointer ${isDark ? 'bg-white/[0.06] hover:bg-white/[0.12] text-slate-200 border border-white/10' : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'}`}
                      title="Snap a photo of the door-jamb sticker — we'll OCR it and pull the specs"
                      data-testid="tow-sticker-upload-label"
                    >
                      <Camera className="w-3 h-3" />
                      {stickerBusy ? 'Reading sticker…' : 'Or upload door-jamb photo'}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleStickerUpload}
                        disabled={stickerBusy}
                        data-testid="tow-sticker-upload-input"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <FieldWrap label="Year" isDark={isDark}>
                    <Input
                      value={towVehicle.year}
                      onChange={e => setTowVehicle(p => ({ ...p, year: e.target.value }))}
                      onBlur={() => maybeLookupYMM()}
                      placeholder="2022"
                      data-testid="tow-year"
                    />
                  </FieldWrap>
                  <FieldWrap label="Make" isDark={isDark}>
                    <Input
                      value={towVehicle.make}
                      onChange={e => setTowVehicle(p => ({ ...p, make: e.target.value }))}
                      onBlur={() => maybeLookupYMM()}
                      placeholder="Ford"
                      data-testid="tow-make"
                    />
                  </FieldWrap>
                  <FieldWrap label="Model" isDark={isDark}>
                    <Input
                      value={towVehicle.model}
                      onChange={e => setTowVehicle(p => ({ ...p, model: e.target.value }))}
                      onBlur={() => maybeLookupYMM()}
                      placeholder="F-150"
                      data-testid="tow-model"
                    />
                  </FieldWrap>
                  <FieldWrap label="Tow Capacity (lbs)" isDark={isDark}>
                    <Input
                      value={towVehicle.capacity}
                      onChange={e => setTowVehicle(p => ({ ...p, capacity: e.target.value }))}
                      placeholder="10,000"
                      data-testid="tow-capacity"
                    />
                  </FieldWrap>
                </div>

                <FieldWrap label="Payload (lbs)" isDark={isDark}>
                  <Input
                    value={towVehicle.payload}
                    onChange={e => setTowVehicle(p => ({ ...p, payload: e.target.value }))}
                    placeholder="2,000"
                    data-testid="tow-payload"
                  />
                </FieldWrap>

                {(vehicleLookupStatus.notes || vehicleLookupStatus.engine || vehicleLookupStatus.towRange) && (
                  <div
                    className={`text-xs p-3 rounded-lg flex items-start gap-2 ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-blue-50 text-gray-700'}`}
                    data-testid="tow-lookup-notes"
                  >
                    <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#F2A900]" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-[10px] ${
                          vehicleLookupStatus.confidence === 'high'
                            ? (isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-800')
                            : vehicleLookupStatus.confidence === 'medium'
                            ? (isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-800')
                            : (isDark ? 'bg-slate-500/20 text-slate-300' : 'bg-slate-200 text-slate-700')
                        }`}>
                          {(vehicleLookupStatus.confidence || 'auto')} confidence
                        </span>
                        {vehicleLookupStatus.engine ? (
                          <span className="text-[11px] opacity-80">{vehicleLookupStatus.engine}{vehicleLookupStatus.driveType ? ` \u00b7 ${vehicleLookupStatus.driveType}` : ''}</span>
                        ) : null}
                      </div>
                      {vehicleLookupStatus.towRange ? (
                        <div className="text-[11px] mb-1">
                          <span className="font-semibold">Tow range:</span> {vehicleLookupStatus.towRange}
                          {vehicleLookupStatus.gcwr ? ` \u00b7 GCWR ~${Number(vehicleLookupStatus.gcwr).toLocaleString()} lbs` : ''}
                        </div>
                      ) : null}
                      {vehicleLookupStatus.notes ? (
                        <div className="text-[11px] leading-snug opacity-90">{vehicleLookupStatus.notes}</div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            )}
          </SectionCard>
        )}

        {/* Tow-check completion CTA — appears in the tow-first flow
            once the user has a decoded vehicle. Reveals the rest of
            the form (Contact, RV Type, Budget, ...) on click. */}
        {towFirstMode && towVehicle.year && towVehicle.make && towVehicle.model && (
          <div
            className={`rounded-2xl border-2 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${isDark ? 'border-emerald-500/40 bg-emerald-500/[0.06]' : 'border-emerald-300 bg-emerald-50'}`}
            data-testid="tow-check-continue-card"
          >
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-black uppercase tracking-widest ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                Nice — we got your tow specs.
              </p>
              <p className={`text-base sm:text-lg font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {towVehicle.year} {towVehicle.make} {towVehicle.model}
                {towVehicle.capacity ? ` \u00b7 ${Number(towVehicle.capacity).toLocaleString()} lbs tow` : ''}
              </p>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                Ready for the rest? Answer a few quick questions and we\u2019ll match you to RVs your truck can actually pull — with prices and payments that fit.
              </p>
            </div>
            <button
              type="button"
              onClick={continueFromTowCheck}
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-[#F2A900] to-amber-500 text-black hover:brightness-110 shadow-lg"
              data-testid="tow-check-continue-btn"
            >
              Continue to your RV match →
            </button>
          </div>
        )}

        {/* 6. Trade-In */}
        {show.trade && (
        <SectionCard icon={RotateCcw} title="Current RV / Trade-In" subtitle="Trading something in? Tell us about it" isDark={isDark} accent="rose">
          <FieldLabel isDark={isDark}>Do you have a trade-in?</FieldLabel>
          <YesNoToggle value={hasTrade} onChange={setHasTrade} isDark={isDark} testIdPrefix="trade" />

          {hasTrade && (
            <div className="space-y-4 mt-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <FieldWrap label="Year" isDark={isDark}>
                  <Input value={tradeInfo.year} onChange={e => setTradeInfo(p => ({ ...p, year: e.target.value }))} placeholder="2018" data-testid="trade-year" />
                </FieldWrap>
                <FieldWrap label="Make" isDark={isDark}>
                  <Input value={tradeInfo.make} onChange={e => setTradeInfo(p => ({ ...p, make: e.target.value }))} placeholder="Keystone" data-testid="trade-make" />
                </FieldWrap>
                <FieldWrap label="Model" isDark={isDark}>
                  <Input value={tradeInfo.model} onChange={e => setTradeInfo(p => ({ ...p, model: e.target.value }))} placeholder="Cougar 26RBS" data-testid="trade-model" />
                </FieldWrap>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldWrap label="Current monthly payment" isDark={isDark}>
                  <Input value={tradeInfo.monthlyPayment} onChange={e => setTradeInfo(p => ({ ...p, monthlyPayment: e.target.value }))} placeholder="$450" data-testid="trade-monthly" />
                </FieldWrap>
                <FieldWrap label="Lienholder" isDark={isDark}>
                  <Input value={tradeInfo.lienholder} onChange={e => setTradeInfo(p => ({ ...p, lienholder: e.target.value }))} placeholder="Bank of America" data-testid="trade-lienholder" />
                </FieldWrap>
              </div>
              <FieldWrap label="What do you like / dislike about it?" isDark={isDark}>
                <textarea
                  value={tradeInfo.notes}
                  onChange={e => setTradeInfo(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Loved the floorplan but want more storage…"
                  rows={3}
                  className={`w-full px-3 py-2 rounded-md border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-[#F2A900]`}
                  data-testid="trade-notes"
                />
              </FieldWrap>
            </div>
          )}
        </SectionCard>
        )}

        {/* 7. Priority Features */}
        {show.priority && (
        <SectionCard icon={Sparkles} title="Most Important to You" subtitle="What matters most when shopping?" isDark={isDark} accent="amber">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PRIORITY_FEATURES.map(f => {
              const Icon = f.icon;
              const active = priorityFeatures.includes(f.id);
              return (
                <BoxOption key={f.id} active={active} onClick={() => toggleFromList(priorityFeatures, setPriorityFeatures, f.id)} isDark={isDark} testId={`priority-${f.id}`}>
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${active ? 'text-[#F2A900]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.label}</span>
                    {active && <Check size={14} className="ml-auto text-[#F2A900]" />}
                  </div>
                </BoxOption>
              );
            })}
          </div>
        </SectionCard>
        )}

        {/* 8. Must-Have RV Features */}
        {show.mustHaves && (
        <SectionCard icon={Home} title="Must-Have RV Features" subtitle="Pick all that are non-negotiable" isDark={isDark} accent="purple">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {MUST_HAVE_FEATURES.map(f => {
              const Icon = f.icon;
              const active = mustHaves.includes(f.id);
              return (
                <BoxOption key={f.id} active={active} onClick={() => toggleFromList(mustHaves, setMustHaves, f.id)} isDark={isDark} testId={`must-${f.id}`}>
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${active ? 'text-[#F2A900]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.label}</span>
                    {active && <Check size={14} className="ml-auto text-[#F2A900]" />}
                  </div>
                </BoxOption>
              );
            })}
          </div>
        </SectionCard>
        )}

        {/* 8b. Floor Plan Preferences — multi-select layouts the buyer loves.
            Sits right after Must-Haves so the AI matcher can score against
            both feature list + layout preference in the same pass. */}
        {show.mustHaves && (
        <SectionCard
          icon={Home}
          title="Preferred Floor Plans"
          subtitle="Pick any layouts you love (multi-select — optional)"
          isDark={isDark}
          accent="rose"
          eduTopic="floor_plans"
          onEduOpen={openEdu}
          eduLabel="What are these?"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {FLOOR_PLAN_OPTIONS.map(f => {
              const active = floorPlans.includes(f.id);
              return (
                <BoxOption key={f.id} active={active} onClick={() => toggleFromList(floorPlans, setFloorPlans, f.id)} isDark={isDark} testId={`floor-plan-${f.id}`}>
                  <div className="flex items-center gap-2">
                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{f.label}</span>
                    {active && <Check size={14} className="ml-auto text-[#F2A900]" />}
                  </div>
                </BoxOption>
              );
            })}
          </div>
        </SectionCard>
        )}

        {/* 9. Notes */}
        {show.notes && (
        <SectionCard icon={FileText} title="Anything Else?" subtitle="Optional — share anything that helps us match better" isDark={isDark} accent="blue">
          <textarea
            value={additionalNotes}
            onChange={e => setAdditionalNotes(e.target.value)}
            placeholder="e.g. We tow with a half-ton truck, want to spend most weekends in the mountains, and need a quiet generator…"
            rows={4}
            className={`w-full px-3 py-2 rounded-md border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-[#F2A900]`}
            data-testid="additional-notes"
          />
        </SectionCard>
        )}

        {/* Submit */}
        {show.notes && (
        <div className="pt-2">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-[#F2A900] hover:bg-[#E89400] text-black font-bold py-6 text-lg rounded-2xl shadow-lg disabled:opacity-60"
            data-testid="submit-application"
          >
            {isSubmitting ? (
              <span className="inline-flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Finding your matches…</span>
            ) : (
              <span className="inline-flex items-center gap-2">Get My RV Matches <ChevronRight className="w-5 h-5" /></span>
            )}
          </Button>
          <p className={`text-xs text-center mt-3 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            By submitting you agree to our terms. No spam, unsubscribe anytime.
          </p>
        </div>
        )}
        </div>
      </section>

      {/* Educational lightbox — opens over the form when a user taps
          the "Learn more" pill next to a section header. */}
      <EducationalLightbox
        topic={eduTopic}
        open={!!eduTopic}
        onClose={closeEdu}
        isDark={isDark}
      />
    </div>
  );
};

const FieldLabel = ({ isDark, children }) => (
  <p className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{children}</p>
);

const FieldWrap = ({ label, children, isDark, className = '' }) => (
  <div className={className}>
    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
    {children}
  </div>
);

const YesNoToggle = ({ value, onChange, isDark, testIdPrefix, iconYes: IconYes }) => (
  <div className="grid grid-cols-2 gap-3">
    <button
      type="button"
      onClick={() => onChange(true)}
      data-testid={`${testIdPrefix}-yes`}
      className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2 ${
        value === true
          ? 'border-[#F2A900] bg-[#F2A900]/10'
          : isDark ? 'border-gray-700 hover:border-gray-500 bg-gray-800/40' : 'border-gray-200 hover:border-gray-400 bg-white'
      }`}
    >
      {IconYes && <IconYes className={`w-4 h-4 ${value === true ? 'text-[#F2A900]' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />}
      <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Yes</span>
    </button>
    <button
      type="button"
      onClick={() => onChange(false)}
      data-testid={`${testIdPrefix}-no`}
      className={`p-3 rounded-xl border-2 transition-all flex items-center justify-center ${
        value === false
          ? 'border-[#F2A900] bg-[#F2A900]/10'
          : isDark ? 'border-gray-700 hover:border-gray-500 bg-gray-800/40' : 'border-gray-200 hover:border-gray-400 bg-white'
      }`}
    >
      <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>No</span>
    </button>
  </div>
);

const FormProgress = ({ show, isTowable, isDark }) => {
  // Order matches the form's actual section order. Tow is conditional on towable RV.
  const stages = [
    { key: 'contact',   label: 'Contact' },
    { key: 'rvType',    label: 'RV Type' },
    { key: 'rvLength',  label: 'Length' },
    { key: 'budget',    label: 'Budget' },
    { key: 'camping',   label: 'Travel' },
    ...(isTowable ? [{ key: 'tow', label: 'Tow Vehicle' }] : []),
    { key: 'trade',     label: 'Trade-In' },
    { key: 'priority',  label: 'Priorities' },
    { key: 'mustHaves', label: 'Must-Haves' },
    { key: 'notes',     label: 'Submit' },
  ];

  // A stage is "active" when the NEXT stage is shown (meaning current is done).
  const completedCount = stages.filter((s, i) => {
    const next = stages[i + 1];
    return next ? !!show[next.key] : !!show[s.key];
  }).length;
  const total = stages.length;
  const pct = Math.round(((completedCount) / total) * 100);

  return (
    <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-2 pb-3 backdrop-blur-md mb-2"
         style={{ background: isDark ? 'rgba(3, 7, 18, 0.85)' : 'rgba(249, 250, 251, 0.9)' }}
         data-testid="form-progress">
      <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`}>
        <div
          className="h-full bg-gradient-to-r from-[#F2A900] to-[#E89400] transition-all duration-500 rounded-full"
          style={{ width: `${pct}%` }}
          data-testid="form-progress-pct"
          data-pct={pct}
        />
      </div>
    </div>
  );
};

export default LandingPage;
